import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { Button, Modal } from 'react-bootstrap';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { loadDeck, shuffleDeck, cardStyle, availableCards } from '../CardData'
import materialsImg from '../assets/materials.png';
import AudioManager from '../audio/AudioManager';
import { CapacitorGameConnect } from '@openforge/capacitor-game-connect';
import TutorialOverlay, { shouldShowTutorial } from './Tutorial';
import './Game.css';
import '../styles/mobile-drag-fix.css';
import '../styles/accessibility.css';

const leaderboardID = 'allTimeHighScore';

// Function to generate a unique ID for a card
const generateUniqueCardId = (card, prefix = '') => {
  return `${prefix}${card.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Check if we're on a mobile platform (iOS or Android)
const isMobilePlatform = () => {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android';
};

const Game = ({ onExit }) => {
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(() => shouldShowTutorial('solitaire'));
  
  // eslint-disable-next-line no-unused-vars
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState('');
  
  // State initialization
  const [cardsAllowedToPlay, setCardsAllowedToPlay] = useState(10);
  const [deck, setDeck] = useState(() => {
    // Initialize deck with unique IDs for each card
    return shuffleDeck(loadDeck()).map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'deck-')
    }));
  });
  const [hand, setHand] = useState([]);
  const [playedCards, setPlayedCards] = useState([]);
  const [discardPile, setDiscardPile] = useState([]);
  const [turnsTaken, setTurnsTaken] = useState(0);
  const [scavengerModalVisible, setScavengerModalVisible] = useState(false);
  const [selectedScavengerCards, setSelectedScavengerCards] = useState([]);
  const [showCardSelectionModal, setShowCardSelectionModal] = useState(false);
  const [showFactoryModal, setShowFactoryModal] = useState(false);
  const [factoryChoices, setFactoryChoices] = useState([]);
  const [thrivingSpiritModalVisible, setThrivingSpiritModalVisible] = useState(false);
  const [selectedThrivingSpiritDiscardCards, setSelectedThrivingSpiritDiscardCards] = useState([]);
  const [selectedThrivingSpiritDeckCards, setSelectedThrivingSpiritDeckCards] = useState([]);
  const [recycleModalVisible, setRecycleModalVisible] = useState(false);
  const [selectedRecycleCards, setSelectedRecycleCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showCardModal, setShowCardModal] = useState(false);
  const [selectedCardIsInHand, setSelectedCardIsInHand] = useState(false);
  const [libraryModalVisible, setLibraryModalVisible] = useState(false);
  const [selectedLibraryCards, setSelectedLibraryCards] = useState([]);
  
  // Drag and drop state
  const [draggedCard, setDraggedCard] = useState(null);
  const [draggedCardIndex, setDraggedCardIndex] = useState(null);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [isDraggingToPlay, setIsDraggingToPlay] = useState(false);
  const [cardRefs] = useState({});
  const [cardSpacing, setCardSpacing] = useState(50);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDraggingToDiscard, setIsDraggingToDiscard] = useState(false);
  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [expandedCardPosition, setExpandedCardPosition] = useState(null);
  const [expandedCardOriginalPosition, setExpandedCardOriginalPosition] = useState(null);
  const [isClosingExpanded, setIsClosingExpanded] = useState(false);
  const [showEnergyModal, setShowEnergyModal] = useState(false);
  // Click/drag discrimination
  const [pressStartInfo, setPressStartInfo] = useState(null); // { x, y, time, index }
  const [dragInitiated, setDragInitiated] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  
  // Reference to track animation timers for cleanup
  const animationTimersRef = useRef([]);
  const playedCardsAreaRef = useRef(null);
  
  // Clean up animation timers on unmount
  useEffect(() => {
    return () => {
      animationTimersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Initialize the game
  useEffect(() => {
    // Authenticate with Game Center/Google Play Games
    const authenticateGameServices = async () => {
      try {
        console.log('Authenticating with game services...');
        const authResult = await CapacitorGameConnect.authenticate();
        console.log('Authentication result:', authResult);
      } catch (error) {
        // Don't show errors to users as this is optional and may fail on some platforms
        console.warn('Game services authentication issue (may be normal):', error);
      }
    };
    
    // Authenticate and draw initial hand
    authenticateGameServices();
    drawInitialHand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for game end and submit score to Game Center
  useEffect(() => {
    if (hasGameEnded()) {
      const totalEnergy = calculateEnergy();
      console.log(`Game ended - submitting final score: ${totalEnergy}`);
      submitScoreToGameCenter(totalEnergy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnsTaken, deck.length, hand.length, playedCards]);

  // Global drag tracking
  useEffect(() => {
    const handleGlobalDragOver = (e) => {
      if (draggedCard) {
        e.preventDefault();
        setDragPosition({ x: e.clientX, y: e.clientY });
      }
    };

    if (draggedCard) {
      document.addEventListener('dragover', handleGlobalDragOver);
      return () => {
        document.removeEventListener('dragover', handleGlobalDragOver);
      };
    }
  }, [draggedCard]);

  // Trigger expanded card animation
  useEffect(() => {
    if (expandedCardIndex !== null && expandedCardPosition) {
      // Small delay to trigger CSS transition
      const timer = setTimeout(() => {
        setExpandedCardPosition(null);
      }, 10);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCardIndex]);

  // Function to close expanded card with animation
  const closeExpandedCard = () => {
    if (expandedCardIndex !== null && !isClosingExpanded) {
      setIsClosingExpanded(true);
      setExpandedCardPosition(expandedCardOriginalPosition);
      
      // Wait for animation to complete before resetting
      setTimeout(() => {
        setExpandedCardIndex(null);
        setExpandedCardPosition(null);
        setExpandedCardOriginalPosition(null);
        setIsClosingExpanded(false);
      }, 400);
    }
  };

  // Handle ESC key to close expanded card
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedCardIndex !== null) {
        closeExpandedCard();
      }
    };
    
    if (expandedCardIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCardIndex, isClosingExpanded, expandedCardOriginalPosition]);

  // Calculate optimal card spacing based on window width and number of cards
  useEffect(() => {
    const calculateCardSpacing = () => {
      const cardWidth = 140; // Base card width
      const minSpacing = 35; // Minimum spacing between cards (more overlap)
      const maxSpacing = 180; // Maximum spacing between cards (increased for more spread)
      const containerPadding = 80; // Padding on both sides (reduced for more space)
      const availableWidth = window.innerWidth - containerPadding;
      
      if (hand.length === 0) {
        setCardSpacing(50);
        return;
      }
      
      if (hand.length === 1) {
        setCardSpacing(0);
        return;
      }
      
      // Calculate optimal spacing to use available width
      const totalOverlapWidth = availableWidth - cardWidth;
      const optimalSpacing = totalOverlapWidth / (hand.length - 1);
      
      // Use the optimal spacing, clamped between min and max
      const finalSpacing = Math.max(minSpacing, Math.min(maxSpacing, optimalSpacing));
      setCardSpacing(finalSpacing);
      
      setWindowWidth(window.innerWidth);
    };

    calculateCardSpacing();
    window.addEventListener('resize', calculateCardSpacing);
    
    return () => window.removeEventListener('resize', calculateCardSpacing);
  }, [hand.length]);

  const clearAnimationAfterDelay = (card, setStateFn) => {
    const timerId = setTimeout(() => {
      setStateFn(prevItems => 
        prevItems.map(item => 
          item.id === card.id ? { ...item, isNew: false } : item
        )
      );
      // Remove this timer from the ref array
      animationTimersRef.current = animationTimersRef.current.filter(id => id !== timerId);
    }, 300);
    
    // Add timer to ref array for cleanup
    animationTimersRef.current.push(timerId);
    return timerId;
  };
  
  const handleRecycleSelection = (card) => {
    setSelectedRecycleCards(prev => {
      if (prev.includes(card)) {
        return prev.filter(c => c !== card);
      } else {
        return [...prev, card];
      }
    });
  };
  
  const handleRecycleConfirm = () => {
    // Create materials cards based on selection
    const newMaterialsCards = selectedRecycleCards.map(() => ({
      name: 'Materials',
      type: 'Tech',
      energy: -5,
      imageUrl: materialsImg,
      description: '-5 energy. Required to build',
      id: generateUniqueCardId({ name: 'Materials' }, 'hand-')
    }));
    
    // Add materials cards to hand
    setHand(prevHand => [...prevHand, ...newMaterialsCards]);
    
    // Remove recycled cards from discard pile
    setDiscardPile(prevDiscard => 
      prevDiscard.filter(card => !selectedRecycleCards.includes(card))
    );
    
    // Reset selections and close modal
    setSelectedRecycleCards([]);
    setRecycleModalVisible(false);
  };

  const drawCard = () => {
    if (deck.length > 0) {
      // Store a reference to the card
      const cardToDraw = deck[0];
      
      const newCard = {
        ...cardToDraw,
        id: cardToDraw.id || generateUniqueCardId(cardToDraw, 'hand-'),
        isNew: true // Mark as new
      };
      
      // Remove the card from the deck
      setDeck(prevDeck => prevDeck.slice(1));
      
      // Add the card to hand with a slight delay to ensure deck is updated
      setTimeout(() => {
        setHand(prevHand => {
          // Check if card already exists in hand (by ID) to prevent duplicates
          if (prevHand.some(c => c.id === newCard.id)) {
            console.warn('Prevented duplicate card:', newCard.name);
            return prevHand;
          }
          return [...prevHand, newCard];
        });
        
        // Remove isNew flag after animation
        clearAnimationAfterDelay(newCard, setHand);
      }, 10);
    }
  };
  
  const drawInitialHand = () => {
    // Create initial hand with unique IDs
    const initialHand = deck.slice(0, 5).map(card => ({
      ...card,
      isNew: true
    }));
    
    setHand(initialHand);
    setDeck(deck.slice(5));
    
    // Remove isNew flag after animation
    const timerId = setTimeout(() => {
      setHand(prevHand => 
        prevHand.map(card => ({ ...card, isNew: false }))
      );
      // Remove this timer from the ref array
      animationTimersRef.current = animationTimersRef.current.filter(id => id !== timerId);
    }, 300);
    
    // Add timer to ref array for cleanup
    animationTimersRef.current.push(timerId);
  };

  const generateRandomCard = () => {
    // Use availableCards instead of loadDeck() to get a card not currently in the game
    const usedCardNames = new Set([
      ...deck.map(card => card.name),
      ...hand.map(card => card.name),
      ...playedCards.map(card => card.name),
      ...discardPile.map(card => card.name)
    ]);
    
    // Filter available cards to get only those not in the game
    const availableCardsNotInGame = availableCards.filter(card => !usedCardNames.has(card.name));
    
    // If there are no unique cards left, fall back to using any available card
    const cardPool = availableCardsNotInGame.length > 0 ? availableCardsNotInGame : availableCards;
    
    const randomIndex = Math.floor(Math.random() * cardPool.length);
    const randomCard = cardPool[randomIndex];
    
    // Create a new instance with a unique ID
    return {
      ...randomCard,
      id: generateUniqueCardId(randomCard, 'random-')
    };
  };

 const playCard = (index) => {
   hapticsImpactLight();
   AudioManager.playCardSound();
   const cardToPlay = hand[index];
 
   if (checkPrerequisites(cardToPlay, playedCards)) {
     const playedCard = {
       ...cardToPlay,
       id: generateUniqueCardId(cardToPlay, 'played-'),
       isNew: true // Mark as new
     };
     
     // First update the hand
     setHand(prevHand => prevHand.filter((_, i) => i !== index));
     
     // Then add to played cards
     setTimeout(() => {
       setPlayedCards(prevPlayedCards => [...prevPlayedCards, playedCard]);
       
       clearAnimationAfterDelay(playedCard, setPlayedCards);
       
       // Handle special card effects first so space increases apply
       // before turnsTaken increments (prevents premature game end)
       handleSpecialCardEffects(cardToPlay);
       setTurnsTaken(prevTurns => prevTurns + 1);
       
       setTimeout(() => {
         drawCard();
       }, 50);
     }, 10);
   } else {
     alert('Prerequisites not met for playing this card.');
   }
 };
  
  const handleScavengerSelection = (selectedCards) => {
    // Add selected cards to hand with unique IDs
    const cardsWithUniqueIds = selectedCards.map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'hand-'),
      uniqueId: undefined // Remove the modal-specific uniqueId
    }));
    
    setHand(prevHand => [...prevHand, ...cardsWithUniqueIds]);
    
    // Remove selected cards from discard pile
    setDiscardPile(prevDiscard => 
      prevDiscard.filter(card => !selectedCards.includes(card))
    );
    
    // Reset selection and close modal
    setSelectedScavengerCards([]);
    setScavengerModalVisible(false);
  };
  
  const handleThrivingSpiritSelection = (source, card) => {
    if (source === 'discard') {
      setSelectedThrivingSpiritDiscardCards(prev => {
        if (prev.some(c => c.id === card.id)) {
          return prev.filter(c => c.id !== card.id);
        } else if (prev.length < 2) {
          return [...prev, card];
        }
        return prev;
      });
    } else {
      setSelectedThrivingSpiritDeckCards(prev => {
        if (prev.some(c => c.id === card.id)) {
          return prev.filter(c => c.id !== card.id);
        } else if (prev.length < 2) {
          return [...prev, card];
        }
        return prev;
      });
    }
  };
  
  const handleThrivingSpiritConfirm = () => {
    // Only proceed if at least one card is selected
    if (selectedThrivingSpiritDiscardCards.length === 0 && 
        selectedThrivingSpiritDeckCards.length === 0) {
      return;
    }

    // Add selected cards to hand with unique IDs
    const discardCardsWithUniqueIds = selectedThrivingSpiritDiscardCards.map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'hand-'),
      uniqueId: undefined // Remove the modal-specific uniqueId
    }));
    
    const deckCardsWithUniqueIds = selectedThrivingSpiritDeckCards.map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'hand-'),
      uniqueId: undefined // Remove the modal-specific uniqueId
    }));
    
    setHand(prevHand => [
      ...prevHand,
      ...discardCardsWithUniqueIds,
      ...deckCardsWithUniqueIds
    ]);
    
    // Remove selected cards from discard pile (if any)
    if (selectedThrivingSpiritDiscardCards.length > 0) {
      setDiscardPile(prevDiscard => 
        prevDiscard.filter(card => 
          !selectedThrivingSpiritDiscardCards.some(selected => selected.id === card.id)
        )
      );
    }
    
    // Remove selected cards from deck (if any)
    if (selectedThrivingSpiritDeckCards.length > 0) {
      setDeck(prevDeck => 
        prevDeck.filter(card => 
          !selectedThrivingSpiritDeckCards.some(selected => selected.id === card.id)
        )
      );
    }
    
    // Reset selections and close modal
    setSelectedThrivingSpiritDiscardCards([]);
    setSelectedThrivingSpiritDeckCards([]);
    setThrivingSpiritModalVisible(false);
  };
  
  const calculateLibrarySelections = () => {
    const curiosityCount = playedCards.filter(card => card.name === 'Curiosity').length;
    return 1 + curiosityCount; // Base selection of 1 plus 1 for each Curiosity card
  };
  
  // Add these handler functions
  const handleLibrarySelection = (card) => {
    setSelectedLibraryCards(prev => {
      if (prev.some(c => c.id === card.id)) {
        return prev.filter(c => c.id !== card.id);
      } else if (prev.length < calculateLibrarySelections()) {
        return [...prev, card];
      }
      return prev;
    });
  };
  
  const handleLibraryConfirm = () => {
    // Add selected cards to hand with unique IDs
    const cardsWithUniqueIds = selectedLibraryCards.map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'hand-'),
      uniqueId: undefined // Remove the modal-specific uniqueId
    }));
    
    setHand(prevHand => [...prevHand, ...cardsWithUniqueIds]);
    
    // Remove selected cards from deck
    setDeck(prevDeck => 
      prevDeck.filter(card => 
        !selectedLibraryCards.some(selected => selected.id === card.id)
      )
    );
    
    // Reset selections and close modal
    setSelectedLibraryCards([]);
    setLibraryModalVisible(false);
  };

  const handleSpecialCardEffects = (cardToPlay) => {
    switch (cardToPlay.name) {
      case 'Observation':
        // For Observation cards, we need to ensure we properly draw the second card without duplicating
        if (deck.length > 1) {
          // Store a reference to the card we want to draw
          const cardToDraw = deck[1];
          
          // Create a new card instance with preserved ID or new unique ID
          const newCard = {
            ...cardToDraw,
            id: cardToDraw.id || generateUniqueCardId(cardToDraw, 'hand-'),
            isNew: true
          };
          
          // First update the deck by removing the selected card
          setDeck(prevDeck => {
            const newDeck = [...prevDeck];
            newDeck.splice(1, 1); // Remove the card at index 1
            return newDeck;
          });
          
          // Then add the card to hand after the deck is updated
          setTimeout(() => {
            setHand(prevHand => {
              // Check if card already exists in hand (by ID) to prevent duplicates
              if (prevHand.some(c => c.id === newCard.id)) {
                console.warn('Prevented duplicate card:', newCard.name);
                return prevHand;
              }
              return [...prevHand, newCard];
            });
            
            // Remove isNew flag after animation
            clearAnimationAfterDelay(newCard, setHand);
          }, 50);
        }
        break;
      case 'Explorer':
        // Sequentially draw cards to avoid race conditions
        if (deck.length > 1) {
          // Store a reference to both cards we want to draw
          const card1Reference = deck[1];
          const card2Reference = deck.length > 2 ? deck[2] : null;
          
          // Create a new card instance with preserved ID or new unique ID
          const card1 = {
            ...card1Reference,
            id: card1Reference.id || generateUniqueCardId(card1Reference, 'hand-'),
            isNew: true
          };
          
          // First update to remove card1
          setDeck(prevDeck => {
            const newDeck = [...prevDeck];
            newDeck.splice(1, 1); // Remove the card at index 1
            return newDeck;
          });
          
          setTimeout(() => {
            // Add card1 to hand
            setHand(prevHand => {
              // Check if card already exists in hand (by ID) to prevent duplicates
              if (prevHand.some(c => c.id === card1.id)) {
                console.warn('Prevented duplicate card:', card1.name);
                return prevHand;
              }
              return [...prevHand, card1];
            });
            
            clearAnimationAfterDelay(card1, setHand);
            
            // Now handle card2 if it exists
            if (card2Reference) {
              // We need to get the updated deck to find the right card
              // (what was at index 2 is now at index 1)
              setTimeout(() => {
                setDeck(prevDeck => {
                  if (prevDeck.length > 1) {
                    // Create a new instance of what's now the second card
                    const card2 = {
                      ...prevDeck[1],
                      id: prevDeck[1].id || generateUniqueCardId(prevDeck[1], 'hand-'),
                      isNew: true
                    };
                    
                    // Remove card2 from deck
                    const newDeck = [...prevDeck];
                    newDeck.splice(1, 1);
                    
                    // Add card2 to hand with another slight delay
                    setTimeout(() => {
                      setHand(prevHand => {
                        // Check if card already exists in hand (by ID) to prevent duplicates
                        if (prevHand.some(c => c.id === card2.id)) {
                          console.warn('Prevented duplicate card:', card2.name);
                          return prevHand;
                        }
                        return [...prevHand, card2];
                      });
                      
                      clearAnimationAfterDelay(card2, setHand);
                    }, 50);
                    
                    return newDeck;
                  }
                  return prevDeck;
                });
              }, 50);
            }
          }, 50);
        }
        break;
      case 'Library':
        setLibraryModalVisible(true);
        break;
      case 'Community':
        setCardsAllowedToPlay(prev => prev + 2);
        // Handle community card drawing similar to Observation
        if (deck.length > 1) {
          // Store a reference to the card we want to draw
          const cardToDraw = deck[1];
          
          // Create a new card instance with preserved ID or new unique ID
          const newCard = {
            ...cardToDraw,
            id: cardToDraw.id || generateUniqueCardId(cardToDraw, 'hand-'),
            isNew: true
          };
          
          // First update the deck by removing the selected card
          setDeck(prevDeck => {
            const newDeck = [...prevDeck];
            newDeck.splice(1, 1); // Remove the card at index 1
            return newDeck;
          });
          
          // Then add the card to hand after the deck is updated
          setTimeout(() => {
            setHand(prevHand => {
              // Check if card already exists in hand (by ID) to prevent duplicates
              if (prevHand.some(c => c.id === newCard.id)) {
                console.warn('Prevented duplicate card:', newCard.name);
                return prevHand;
              }
              return [...prevHand, newCard];
            });
            
            // Remove isNew flag after animation
            clearAnimationAfterDelay(newCard, setHand);
          }, 50);
        }
        break;
      case 'Settlement':
        setCardsAllowedToPlay(prev => prev + 5);
        break;
      case 'City':
        setCardsAllowedToPlay(prev => prev + 5);
        break;
      case 'Thriving Civilization':
        setCardsAllowedToPlay(prev => prev + 10);
        const factoryCard = availableCards.find(card => card.name === 'Factory');
        const cityCard = availableCards.find(card => card.name === 'City');
        if (factoryCard && cityCard) {
          // Create new instances with unique IDs
          const newFactoryCard = {
            ...factoryCard,
            id: generateUniqueCardId(factoryCard, 'deck-')
          };
          const newCityCard = {
            ...cityCard,
            id: generateUniqueCardId(cityCard, 'deck-')
          };
          addCardsToDeck([newFactoryCard, newCityCard]);
        }
        break;
      case 'Factory':
        const materialsCount = playedCards.filter(card => card.name === 'Materials').length;
        const choices = materialsCount * 3;
        setFactoryChoices(new Array(choices).fill(null));
        setShowFactoryModal(true);
        break;
      case 'Bloom':
        const cardsToAdd = ['Sapling', 'Tree', 'Forest', 'Wildflowers']
          .map(name => {
            const card = availableCards.find(card => card.name === name);
            if (card) {
              // Create a new instance with a unique ID
              return {
                ...card,
                id: generateUniqueCardId(card, 'deck-')
              };
            }
            return null;
          })
          .filter(card => card !== null); // Filter out any undefined cards
        
        addCardsToDeck(cardsToAdd);
        break;
      case 'Thriving Ecosystem':
        const bloomCard = availableCards.find(card => card.name === 'Bloom');
        if (bloomCard) {
          // Create a new instance with a unique ID
          const newBloomCard = {
            ...bloomCard,
            id: generateUniqueCardId(bloomCard, 'deck-')
          };
          addCardsToDeck([newBloomCard]);
        }
        break;
      case 'Uncharted':
        setCardsAllowedToPlay(prev => prev + 2);
        const randomCard1 = generateRandomCard();
        const randomCard2 = generateRandomCard();
        addCardsToDeck([randomCard1, randomCard2]);
        break;
      case 'Scavenger':
        setScavengerModalVisible(true);
        break;
      case 'Recycle':
        setRecycleModalVisible(true);
        break;
      case 'Futurist':
        setShowCardSelectionModal(true);
        break;
      case 'Oasis':
        setCardsAllowedToPlay(prev => prev + 3);
        break;
      case 'Habitat':
        setCardsAllowedToPlay(prev => prev + 2);
        break;
      case 'Forest':
        break;
      case 'Love':
        setCardsAllowedToPlay(prev => prev + 1);
        break;
      case 'Thriving Spirit':
        setCardsAllowedToPlay(prev => prev + 1);
        setThrivingSpiritModalVisible(true);
        break;

      default:
        break;
    }
  };
  
 const handleFactorySelection = (selectedCards) => {
    // Add cards to deck with unique IDs
    const cardsWithUniqueIds = selectedCards.map(card => ({
      ...card,
      id: generateUniqueCardId(card, 'deck-')
    }));
    
    addCardsToDeck(cardsWithUniqueIds);
    setShowFactoryModal(false);
  };
  
  const addCardsToDeck = (cards) => {
    // Make sure each card has a unique ID before adding to deck
    const cardsWithUniqueIds = cards.map(card => ({
      ...card,
      id: card.id || generateUniqueCardId(card, 'deck-')
    }));
    
    setDeck(prevDeck => {
      const newDeck = [...prevDeck, ...cardsWithUniqueIds];
      return shuffleDeck(newDeck);
    });
  };

  const discardCard = (index) => {
    hapticsImpactLight();
    AudioManager.playDiscardSound();
    
    // Get the card being discarded to maintain its properties
    const cardToDiscard = hand[index];
    
    // Create a new card instance with a new ID for the discard pile
    const discardedCard = {
      ...cardToDiscard,
      id: generateUniqueCardId(cardToDiscard, 'discard-'),
      isNew: false
    };
    
    setDiscardPile(prevDiscardPile => [...prevDiscardPile, discardedCard]);
    setHand(prevHand => prevHand.filter((_, i) => i !== index));
    drawCard();
  };

  const checkPrerequisites = (cardToPlay, playedCards) => {
    const hasCard = cardName => playedCards.some(card => card.name === cardName);

    switch (cardToPlay.type) {
      case 'Eco':
        switch (cardToPlay.name) {
          case 'Sapling':
            return hasCard('Seed');
          case 'Tree':
            return hasCard('Sapling') || (hasCard('Seed') && hasCard('Patience'));
          case 'Forest':
            return hasCard('Tree');
          case 'Garden':
            return (hasCard('Naturalist') && hasCard('Seed')) || (hasCard('Seed') && hasCard('Patience'));
          case 'Habitat':
            return hasCard('Naturalist') || hasCard('Forest');
          case 'Oasis':
            return (hasCard('Explorer') && hasCard('Patience')) || 
                   (hasCard('Explorer') && hasCard('Naturalist'));
          case 'Thriving Ecosystem':
            return hasCard('Forest') && hasCard('Love');
          case 'Bloom':
            return hasCard('Tree');
          case 'Farm':
            return hasCard('Community');
          case 'Uncharted':
            return hasCard('Explorer');
          default:
            return true;
        }

      case 'Tech':
        switch (cardToPlay.name) {
          case 'Battery':
            return hasCard('Tools') && hasCard('Materials');
          case 'Solar Panel':
          case 'Wind Turbine':
            return hasCard('Battery') && hasCard('Engineer');
          case 'Nuclear Energy':
            return hasCard('Battery') && hasCard('Engineer') && hasCard('Physicist');
          case 'Settlement':
            return hasCard('Solar Panel') || hasCard('Wind Turbine') || 
                 hasCard('Nuclear Energy') || 
                    hasCard('Farm');
          case 'City':
            // Check for required base cards
            const hasFactory = hasCard('Factory');
            const hasCommunity = hasCard('Community');
            const hasSettlement = hasCard('Settlement');
                   
            // Count unique energy sources
            const energySources = new Set();
            if (hasCard('Solar Panel')) energySources.add('Solar Panel');
            if (hasCard('Wind Turbine')) energySources.add('Wind Turbine');
            if (hasCard('Nuclear Energy')) energySources.add('Nuclear Energy');
                   
            return hasFactory && (hasCommunity || hasSettlement) && energySources.size >= 2;
          case 'Thriving Civilization':
            return hasCard('City') && hasCard('Love');
          case 'Factory':
            return hasCard('Settlement');
          case 'Recycle':
            const hasScavenger = hasCard('Scavenger');
            const hasEnergy = hasCard('Solar Panel') || 
                           hasCard('Wind Turbine') || 
                           hasCard('Nuclear Energy');
            return hasScavenger && hasEnergy;
          default:
            return true;
        }

      case 'Wisdom':
        switch (cardToPlay.name) {
          case 'Patience':
            return hasCard('Meditation');
          case 'Love':
            return hasCard('Patience');
          case 'Explorer':
          case 'Physicist':
            return hasCard('Curiosity');
          case 'Thriving Spirit':
            return hasCard('Curiosity') && hasCard('Love');
          case 'Community':
            return hasCard('Garden') || hasCard('Love');
          default:
            return true;
        }

      default:
        return false;
    }
  };

  const calculateEnergy = () => {
    return playedCards.reduce((total, card) => total + card.energy, 0);
  };

  const calculateScore = () => {
    const totalEnergy = calculateEnergy();
    const currentHighScore = localStorage.getItem('highScore') ? 
      parseInt(localStorage.getItem('highScore'), 10) : 0;

    // Update local high score if the current score is higher and game has ended
    if (hasGameEnded() && totalEnergy > currentHighScore) {
        localStorage.setItem('highScore', totalEnergy.toString());
    }
    
    // Always return the higher of current energy or high score for display
    return totalEnergy > currentHighScore ? totalEnergy : currentHighScore;
  };

  // Function to handle Game Center score submission with proper error handling
  const submitScoreToGameCenter = async (score) => {
    try {
      // Make sure score is at least 0 to avoid potential issues with negative scores
      const finalScore = Math.max(0, score);
      console.log(`Submitting score to Game Center: ${finalScore}`);
      
      // Debug log to verify the leaderboard ID
      console.log(`Using leaderboard ID: ${leaderboardID}`);
      
      const result = await CapacitorGameConnect.submitScore({ 
        leaderboardID, 
        totalScoreAmount: finalScore 
      });
      
      console.log('Score submitted successfully:', result);
    } catch (error) {
      console.error('Error submitting score to Game Center:', error);
      
      // Try again once more with a slight delay in case of network issues
      setTimeout(() => {
        const retryScore = Math.max(0, score);
        console.log(`Retrying score submission with score: ${retryScore}`);
        
        CapacitorGameConnect.submitScore({ 
          leaderboardID, 
          totalScoreAmount: retryScore 
        }).catch(retryError => 
          console.error('Retry failed:', retryError)
        );
      }, 1000);
    }
  };

  const hasGameEnded = () => {
      return turnsTaken === cardsAllowedToPlay || (deck.length === 0 && hand.length === 0);
    };

  // Drag and drop handlers
  const handleDragStart = (e, card, index) => {
    setDraggedCard(card);
    setDraggedCardIndex(index);
    setDragInitiated(true);
    // Set initial drag position
    setDragPosition({ x: e.clientX, y: e.clientY });
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify({ cardIndex: index }));
      // Create a custom drag image to avoid cropping
      const dragImage = new Image();
      dragImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='; // 1x1 transparent image
      e.dataTransfer.setDragImage(dragImage, 0, 0);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // Update drag position to follow cursor
    if (draggedCard) {
      setDragPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleDragEnd = (e) => {
    // Reset drag states
    setDraggedCard(null);
    setDraggedCardIndex(null);
    setIsDraggingToPlay(false);
    setIsDraggingToDiscard(false);
    setPressStartInfo(null);
    setDragInitiated(false);
    setHoveredCardIndex(null);
  };

const renderCard = (card, index, isInHand, count = 1) => {
      const gameEnded = hasGameEnded();
      const canPlay = isInHand && checkPrerequisites(card, playedCards) && !gameEnded;
  const isHovered = hoveredCardIndex === index;
  const isDragging = draggedCardIndex === index;
  const isDraggedButNotHovered = isDragging && !isHovered && draggedCard;
  const isExpanded = expandedCardIndex === index && isInHand;
  
  if (!isInHand) {
    // Render played cards normally
  return (
 <div
        key={card.id || `card-${index}-played`}
     style={{
          width: '100%',
          minWidth: '0',
          maxWidth: '100%',
          height: 'auto',
           aspectRatio: '1/1',
           backgroundColor: 'rgba(255,255,255,0.95)',
           borderRadius: '10px',
           border: '2px solid rgba(0,0,0,0.08)',
           boxShadow: '0 5px 18px rgba(0, 0, 0, 0.18)',
          cursor: 'pointer',
          position: 'relative',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          margin: 0,
          padding: '0',
          boxSizing: 'border-box',
          overflow: 'hidden',
     }}
     className={card.isNew ? 'game-card card-new' : 'game-card'}
     onClick={(e) => {
          // Get the card's current position for animation
          const cardElement = e.currentTarget;
          if (cardElement) {
            const rect = cardElement.getBoundingClientRect();
            const position = {
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height
            };
            setExpandedCardPosition(position);
            setExpandedCardOriginalPosition(position);
          }
          setExpandedCardIndex(`played-${card.name}`);
          setSelectedCard(card);
          setSelectedCardIndex(null); // Not in hand
          setSelectedCardIsInHand(false);
          setIsClosingExpanded(false);
          hapticsImpactLight();
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.03)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.25)';
          e.currentTarget.style.zIndex = '10';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
          e.currentTarget.style.zIndex = '1';
        }}
      >
        <img src={card.imageUrl} alt={card.name} style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'contain',
                  borderRadius: '10px',
          display: 'block',
        }} />
        <div style={{ 
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          padding: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '0 0 10px 10px',
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '11px', textAlign: 'center', lineHeight: '1.2', color: '#fff' }}>{card.name}</div>
        </div>
        {count > 1 && (
          <div style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            backgroundColor: 'rgba(255, 0, 0, 0.9)',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
            border: '2px solid white',
          }}>
            {count}
          </div>
        )}
      </div>
    );
  }
  
  const centerIndex = (hand.length - 1) / 2;
  const distanceFromCenter = index - centerIndex;
  const rotationAngle = distanceFromCenter * 2; // Slight fan effect
  
  // Calculate vertical offset for fan effect (middle cards higher)
  const normalizedDistance = Math.abs(distanceFromCenter) / (hand.length / 2);
  const verticalOffset = (1 - normalizedDistance) * 15; // Max 15px higher in middle
  
  const handCardStyle = {
    ...cardStyle,
    width: '140px',
    height: '200px',
    position: 'absolute',
    left: `${index * cardSpacing}px`,
    top: 'auto',
    bottom: '25px',
    transition: isDragging ? 'none' : isExpanded ? 'opacity 0.3s ease' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isDragging
      ? `scale(1.3) rotate(-5deg)` // Being dragged
      : isHovered && draggedCard
        ? `translateY(${-30 - verticalOffset}px) scale(1.15) rotate(0deg)` // Hovered during drag
        : isDraggedButNotHovered
          ? `translateY(${-10 - verticalOffset}px) scale(0.95) rotate(${rotationAngle * 0.5}deg)` // Original card being dragged but not hovered
          : isHovered && !gameEnded
            ? `translateY(${-20 - verticalOffset}px) scale(1.08) rotate(0deg)` // Normal hover
            : `translateY(${-verticalOffset}px) scale(1) rotate(${rotationAngle}deg)`, // Normal state
    transformOrigin: isDragging ? 'center' : 'center bottom',
    zIndex: isDragging ? 3000 : isHovered ? 2000 : index,
    cursor: isDragging ? 'grabbing' : canPlay ? 'grab' : 'not-allowed',
    pointerEvents: 'auto',
    opacity: (isExpanded && !isClosingExpanded) ? 0 : isDragging ? 0 : isDraggedButNotHovered ? 0.7 : (gameEnded ? 0.6 : 1),
    filter: isDragging
      ? 'brightness(1.2) drop-shadow(0 0 30px rgba(255, 255, 255, 0.8))' 
      : isHovered && draggedCard 
        ? 'brightness(1.15) drop-shadow(0 0 20px rgba(255, 255, 255, 0.5))' 
        : isHovered 
          ? 'brightness(1.1)' 
          : isDraggedButNotHovered 
            ? 'brightness(0.8)' 
            : 'brightness(1)',
    boxShadow: isDragging ?
      '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 60px rgba(255, 255, 255, 0.4)' :
      isHovered && draggedCard ?
        (canPlay ? '0 30px 80px rgba(0, 255, 0, 1), 0 0 100px rgba(0, 255, 0, 0.8), inset 0 0 30px rgba(255, 255, 255, 0.3)' :
                   '0 30px 80px rgba(255, 0, 0, 1), 0 0 100px rgba(255, 0, 0, 0.8), inset 0 0 30px rgba(255, 255, 255, 0.3)') :
        isHovered ? 
          (canPlay ? '0 20px 60px rgba(0, 255, 0, 0.9), 0 0 80px rgba(0, 255, 0, 0.7), inset 0 0 20px rgba(255, 255, 255, 0.2)' : 
                     '0 20px 60px rgba(255, 0, 0, 0.9), 0 0 80px rgba(255, 0, 0, 0.7), inset 0 0 20px rgba(255, 255, 255, 0.2)') :
          isDraggedButNotHovered ?
            '0 2px 10px rgba(0, 0, 0, 0.2)' : // Subtle shadow for dragged but not hovered
            (canPlay ? '0 5px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 255, 0, 0.3)' : 
                       '0 5px 15px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 0, 0, 0.3)'),
  };
  
  return (
    <div
      key={card.id || `card-${index}-hand`}
      ref={(el) => cardRefs[index] = el}
      style={handCardStyle}
      className={`game-card ${card.isNew ? 'card-new' : ''} ${isDraggedButNotHovered ? 'dragging-away' : ''} ${isHovered && draggedCard ? 'dragging' : ''} ${isDragging ? 'dragging-active' : ''}`}
      draggable={canPlay}
      onMouseDown={(e) => {
        setPressStartInfo({ x: e.clientX, y: e.clientY, time: Date.now(), index });
      }}
      onMouseUp={(e) => {
        // Treat as click if no drag was initiated
        if (!dragInitiated && (!draggedCard)) {
          const cardElement = e.currentTarget;
          if (cardElement) {
            const rect = cardElement.getBoundingClientRect();
            const position = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
            setExpandedCardPosition(position);
            setExpandedCardOriginalPosition(position);
          }
          setExpandedCardIndex(index);
          setSelectedCard(card);
          setSelectedCardIndex(index);
          setSelectedCardIsInHand(true);
          setIsClosingExpanded(false);
          hapticsImpactLight();
        }
        setPressStartInfo(null);
        setDragInitiated(false);
      }}
      onDragStart={(e) => handleDragStart(e, card, index)}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => !gameEnded && setHoveredCardIndex(index)}
      onMouseLeave={() => !gameEnded && setHoveredCardIndex(null)}
      onClick={() => { /* Click handled on mouseup to avoid conflict with drag */ }}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        setPressStartInfo({ x: touch.clientX, y: touch.clientY, time: Date.now(), index });
        setHoveredCardIndex(index);
      }}
      onTouchMove={(e) => {
        if (!pressStartInfo) return;
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - pressStartInfo.x);
        const dy = Math.abs(touch.clientY - pressStartInfo.y);
        const movedEnough = dx + dy > 10;
        if (movedEnough && !dragInitiated) {
          // Start drag programmatically for touch
          setDragInitiated(true);
          handleDragStart(e, card, index);
        }
      }}
      onTouchEnd={(e) => {
        // If no drag, treat as tap to open
        if (!dragInitiated && (!draggedCard)) {
          const cardElement = e.currentTarget;
          if (cardElement) {
            const rect = cardElement.getBoundingClientRect();
            const position = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
            setExpandedCardPosition(position);
            setExpandedCardOriginalPosition(position);
          }
          setExpandedCardIndex(index);
          setSelectedCard(card);
          setSelectedCardIndex(index);
          setSelectedCardIsInHand(true);
          setIsClosingExpanded(false);
          hapticsImpactLight();
        }
        handleDragEnd(e);
      }}
    >
      <img 
        src={card.imageUrl} 
        alt={card.name} 
                style={{
          width: '100%', 
          height: '65%', 
          objectFit: 'cover',
          borderRadius: '10px 10px 0 0',
          pointerEvents: 'none'
        }} 
      />
      <div style={{ 
        padding: '5px', 
        height: '35%', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{card.name}</div>
        <div style={{ fontSize: '10px', lineHeight: '1.2' }}>{card.description}</div>
        </div>
      </div>
    );
  };

  const hapticsImpactLight = async () => {
    // Only use haptics on mobile platforms (iOS and Android)
    if (isMobilePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (error) {
        console.warn('Haptics not available:', error);
      }
    }
  };
  
  // Function for the Futurist card to properly select a card with a unique ID
  const handleFuturistSelection = (selectedCard) => {
    // Create a new card instance with a unique ID
    const cardWithUniqueId = {
      ...selectedCard,
      id: generateUniqueCardId(selectedCard, 'hand-')
    };
    
    // Add to hand with unique ID
    setHand(prev => [...prev, cardWithUniqueId]);
    
    // Remove from deck
    setDeck(prev => {
      const newDeck = prev.filter(card => card.id !== selectedCard.id);
      return shuffleDeck(newDeck);
    });
    
    setShowCardSelectionModal(false);
  };

  return (
    <div 
      className="Game page-transition" 
      role="main"
      aria-label="Neolysium Solitaire Mode Game"
      style={{ 
        minHeight: '100vh',
      }}
    >
    {/* Screen Reader Announcements - Live Region */}
    <div 
      role="status" 
      aria-live="polite" 
      aria-atomic="true"
      className="sr-only"
    >
      {screenReaderAnnouncement}
    </div>
    
    {/* Tutorial Overlay for first-time players */}
    {showTutorial && (
      <TutorialOverlay 
        mode="solitaire" 
        onComplete={() => setShowTutorial(false)}
        onSkip={() => setShowTutorial(false)}
      />
    )}
    
    <div className="blur" style={{ 
      minHeight: '100vh',
      padding: '20px',
    }}>

      

      {hasGameEnded() && (
        <div className="gameOver">
          <strong className="appText">Game Over! Final Energy Score: {calculateEnergy()}</strong>
          <br />
          <button 
            style={{
              marginTop: '10px',
              height: '40px',
              color: 'black',
              backgroundColor: 'white',
              borderRadius: '10px',
              fontFamily: 'Orbitron'
            }}
            onClick={onExit}
          >
            Home
          </button>
          <GameCenterButtons leaderboardID={leaderboardID} />
        </div>
      )}

      <div className="header" style={{ 
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: windowWidth < 768 ? '20px 20px' : '30px 30px',
        gap: windowWidth < 768 ? '8px' : '20px',
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'visible',
        WebkitOverflowScrolling: 'touch',
        width: 'fit-content',
        maxWidth: '100%',
        margin: '30px auto 20px auto',
      }}>
        {/* Energy Score Pill */}
        <div
          style={{
            flex: '0 0 auto',
            minWidth: windowWidth < 768 ? '90px' : '140px',
            maxWidth: windowWidth > 1200 ? '300px' : 'none',
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
            border: '2px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '50px',
            padding: windowWidth < 768 ? '8px 12px' : '12px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(255, 193, 7, 0.2), inset 0 0 20px rgba(255, 193, 7, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
          }}
          onClick={() => setShowEnergyModal(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 30px rgba(255, 193, 7, 0.3), inset 0 0 30px rgba(255, 193, 7, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 193, 7, 0.2), inset 0 0 20px rgba(255, 193, 7, 0.1)';
          }}
        >

          <svg 
            width={windowWidth < 768 ? '16' : '20'} 
            height={windowWidth < 768 ? '16' : '20'} 
            viewBox="0 0 24 24" 
            fill="none"
            style={{ 
              marginRight: windowWidth < 768 ? '6px' : '10px',
              zIndex: 1,
              filter: 'drop-shadow(0 0 4px rgba(255, 193, 7, 0.5))',
            }}
            title="Energy Score"
          >
            <path 
              d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" 
              fill="rgba(255, 193, 7, 0.8)"
              stroke="rgba(255, 193, 7, 1)"
              strokeWidth="1"
            />
          </svg>
          <div style={{ 
            fontSize: windowWidth < 768 ? '18px' : '24px', 
            color: '#ffc107', 
            fontWeight: 'bold',
            textShadow: '0 0 10px rgba(255, 193, 7, 0.5)',
            zIndex: 1,
          }}>
            {calculateEnergy()}
          </div>
      </div>

        {/* Cards Played Progress Pill */}
        <div style={{
          flex: '0 0 auto',
          minWidth: windowWidth < 768 ? '110px' : '180px',
          maxWidth: windowWidth < 768 ? '110px' : windowWidth > 1200 ? '250px' : '180px',
          background: 'linear-gradient(135deg, rgba(0, 255, 127, 0.1) 0%, rgba(0, 200, 83, 0.1) 100%)',
          border: '2px solid rgba(0, 255, 127, 0.3)',
          borderRadius: '50px',
          padding: windowWidth < 768 ? '8px 12px' : '12px 24px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0, 255, 127, 0.2), inset 0 0 20px rgba(0, 255, 127, 0.1)',
          position: 'relative',
          overflow: 'visible',
        }}>

          <svg 
            width={windowWidth < 768 ? '16' : '20'} 
            height={windowWidth < 768 ? '16' : '20'} 
            viewBox="0 0 24 24" 
            fill="none"
            style={{ 
              marginRight: windowWidth < 768 ? '6px' : '10px',
              zIndex: 1,
              filter: 'drop-shadow(0 0 4px rgba(0, 255, 127, 0.5))',
            }}
            title="Cards Played"
          >
            <rect x="5" y="4" width="10" height="14" rx="1" fill="rgba(0, 255, 127, 0.6)" stroke="rgba(0, 255, 127, 0.8)" strokeWidth="1"/>
            <rect x="8" y="2" width="10" height="14" rx="1" fill="rgba(0, 255, 127, 0.8)" stroke="rgba(0, 255, 127, 1)" strokeWidth="1"/>
          </svg>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 1,
          }}>
            <div style={{ 
              fontSize: windowWidth < 768 ? '16px' : '20px', 
              color: '#00ff7f', 
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(0, 255, 127, 0.5)',
              marginBottom: windowWidth < 768 ? '2px' : '4px',
            }}>
              {playedCards.length} / {cardsAllowedToPlay}
            </div>
            {/* Progress Bar */}
            <div style={{
              width: windowWidth < 768 ? '60px' : '80px',
              height: windowWidth < 768 ? '3px' : '4px',
              backgroundColor: 'rgba(0, 255, 127, 0.1)',
              borderRadius: '2px',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{
                width: `${(playedCards.length / cardsAllowedToPlay) * 100}%`,
                height: '100%',
                background: `linear-gradient(90deg, #00ff7f 0%, ${
                  (cardsAllowedToPlay - playedCards.length) === 1 ? '#ff6b6b' :
                  (cardsAllowedToPlay - playedCards.length) === 2 ? '#ffa500' :
                  '#00ff7f'
                } 100%)`,
                borderRadius: '2px',
                transition: 'all 0.3s ease',
                boxShadow: `0 0 10px ${
                  (cardsAllowedToPlay - playedCards.length) === 1 ? 'rgba(255, 107, 107, 0.5)' :
                  (cardsAllowedToPlay - playedCards.length) === 2 ? 'rgba(255, 165, 0, 0.5)' :
                  'rgba(0, 255, 127, 0.5)'
                }`,
              }} />
            </div>
          </div>
        </div>

        {/* Deck Remaining Pill */}
        <div style={{
          flex: windowWidth < 768 ? '0 0 auto' : '1 1 0',
          minWidth: windowWidth < 768 ? '110px' : '180px',
          maxWidth: windowWidth < 768 ? '110px' : windowWidth > 1200 ? '250px' : '180px',
          background: 'linear-gradient(135deg, rgba(135, 206, 235, 0.1) 0%, rgba(100, 149, 237, 0.1) 100%)',
          border: '2px solid rgba(135, 206, 235, 0.3)',
          borderRadius: '50px',
          padding: windowWidth < 768 ? '8px 12px' : '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(135, 206, 235, 0.2), inset 0 0 20px rgba(135, 206, 235, 0.1)',
          position: 'relative',
          overflow: 'visible',
        }}>

          <svg 
            width={windowWidth < 768 ? '16' : '20'} 
            height={windowWidth < 768 ? '16' : '20'} 
            viewBox="0 0 24 24" 
            fill="none"
            style={{ 
              marginRight: windowWidth < 768 ? '6px' : '10px',
              zIndex: 1,
              filter: 'drop-shadow(0 0 4px rgba(135, 206, 235, 0.5))',
            }}
            title="Cards in Deck"
          >
            <rect x="3" y="6" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.3)" stroke="rgba(135, 206, 235, 0.5)" strokeWidth="1" transform="rotate(-5 9 14)"/>
            <rect x="5" y="5" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.5)" stroke="rgba(135, 206, 235, 0.7)" strokeWidth="1" transform="rotate(-2 11 13)"/>
            <rect x="7" y="4" width="12" height="16" rx="1" fill="rgba(135, 206, 235, 0.8)" stroke="rgba(135, 206, 235, 1)" strokeWidth="1"/>
            <circle cx="13" cy="12" r="3" fill="rgba(255, 255, 255, 0.3)"/>
          </svg>
          <div style={{ 
            fontSize: windowWidth < 768 ? '18px' : '24px', 
            color: '#87ceeb', 
            fontWeight: 'bold',
            textShadow: '0 0 10px rgba(135, 206, 235, 0.5)',
            zIndex: 1,
          }}>
            {deck.length}
          </div>
        </div>
      </div>

      <div 
        ref={playedCardsAreaRef}
        className="played-cards-area" 
        style={{
        marginBottom: '20px',
          height: 'calc(100vh - 460px)',
          minHeight: '250px',
          maxHeight: '70vh',
          backgroundColor: isDraggingToPlay ? 'rgba(0, 255, 0, 0.25)' : 'rgba(0, 255, 0, 0.125)',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.10)',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
          transition: 'background-color 0.2s ease',
          border: 'none',
          overflow: 'hidden',
          position: 'relative',
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDraggingToPlay(true);
        }}
        onDragOver={(e) => {
          // Required so drop can fire
          e.preventDefault();
          setIsDraggingToPlay(true);
        }}
        onDragLeave={() => {
          setIsDraggingToPlay(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDraggingToPlay(false);
          if (draggedCardIndex !== null) {
            const canPlay = checkPrerequisites(draggedCard, playedCards);
            if (canPlay) {
              playCard(draggedCardIndex);
            } else {
              alert('Prerequisites not met for playing this card.');
            }
          }
          // Reset all drag states
          setDraggedCard(null);
          setDraggedCardIndex(null);
          setIsDraggingToDiscard(false);
          setDragInitiated(false);
          setHoveredCardIndex(null);
        }}
      >

        <div style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          padding: '20px 10px',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          position: 'relative',
        }}>
          <div 
            className="played-cards-grid"
            style={{ 
              display: 'grid', 
              gridTemplateColumns: windowWidth >= 1200 ? 'repeat(6, 1fr)' : 'repeat(4, 1fr)', 
              gap: windowWidth >= 1200 ? '10px' : '15px', 
              justifyItems: 'stretch', 
              alignItems: 'start',
              width: '100%',
              boxSizing: 'border-box',
              maxWidth: '1100px',
              margin: '0 auto',
              paddingBottom: '40px',
              gridAutoRows: 'minmax(0, auto)',
              gridAutoFlow: 'row',
              isolation: 'isolate',
            }}>
          {(() => {
            // Group duplicate cards while preserving order of most recent
            const cardGroups = {};
            const cardOrder = [];
            
            // Process cards in reverse order (most recent first)
            [...playedCards].reverse().forEach((card) => {
              const key = card.name;
              if (!cardGroups[key]) {
                cardGroups[key] = { card, count: 1 };
                cardOrder.push(key);
              } else {
                cardGroups[key].count++;
              }
            });
            
            // Calculate remaining slots
            const remainingSlots = cardsAllowedToPlay - playedCards.length;
            const emptySlots = [];
            
            // Add warning slots if needed
            if (remainingSlots > 0 && remainingSlots <= 2) {
              for (let i = 0; i < remainingSlots; i++) {
                const isLastSlot = remainingSlots === 1 || (remainingSlots === 2 && i === 1);
                
                emptySlots.push(
                  <div
                    key={`empty-slot-${i}`}
                    className={isLastSlot ? 'warning-slot-critical' : 'warning-slot'}
                    style={{
                      width: '100%',
                      aspectRatio: '3/4',
                      backgroundColor: isDraggingToPlay 
                        ? (isLastSlot ? 'rgba(255, 0, 0, 0.3)' : 'rgba(255, 165, 0, 0.3)')
                        : (isLastSlot ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 165, 0, 0.1)'),
                      border: isDraggingToPlay
                        ? (isLastSlot ? '3px solid rgba(255, 0, 0, 0.8)' : '3px solid rgba(255, 165, 0, 0.8)')
                        : (isLastSlot ? '2px solid rgba(255, 0, 0, 0.4)' : '2px solid rgba(255, 165, 0, 0.4)'),
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '36px',
                      fontWeight: 'bold',
                      color: isLastSlot ? 'rgba(255, 0, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)',
                      boxSizing: 'border-box',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                  >

                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.2 }}>
                      <path 
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" 
                        fill={isLastSlot ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 165, 0, 0.5)'}
                      />
                    </svg>
                  </div>
                );
              }
            }
            
            // Render grouped cards in order (most recent first)
            const renderedCards = cardOrder.map((cardName, index) => {
              const group = cardGroups[cardName];
              return renderCard(group.card, `played-${cardName}-${index}`, false, group.count);
            });
            
            return [...renderedCards, ...emptySlots];
          })()}
          </div>
        </div>
      </div>

      <div style={{
          height: '20px',
        }}> </div>
      
      <div className="hand-area" 
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '0',
          right: '0',
          height: '240px',
          backgroundColor: 'transparent',
          zIndex: 100,
          padding: '0',
          overflow: 'visible',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          handleDragOver(e);
        }}
        onDragLeave={() => {}}
      >

        <div style={{ 
          position: 'relative',
          width: '100%',
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '0',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'relative',
            width: `${(hand.length - 1) * cardSpacing + 140}px`,
            height: '200px',
            margin: '0 auto',
            overflow: 'visible'
          }}>
          {hand.map((card, index) => renderCard(card, index, true))}
        </div>
      </div>
        {isDraggingToPlay && !hasGameEnded() && (
      <div style={{
            position: 'absolute',
            top: '-120px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 255, 0, 0.3)',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#00ff00',
            textShadow: '0 0 10px rgba(0, 255, 0, 0.8), 0 0 20px rgba(0, 255, 0, 0.6)',
            border: '3px solid rgba(0, 255, 0, 0.7)',
            boxShadow: '0 0 20px rgba(0, 255, 0, 0.5)'
          }}>
            ↑ Release to Play ↑
          </div>
        )}
      </div>

      {/* Discard Drop Zone */}
      {draggedCard && !hasGameEnded() && (
        <div
          style={{
            position: 'fixed',
            bottom: 'min(350px, 50vh)',
            right: 'clamp(10px, 3vw, 30px)',
            width: 'clamp(80px, 15vw, 120px)',
            height: 'clamp(80px, 15vw, 120px)',
            borderRadius: '50%',
            backgroundColor: isDraggingToDiscard ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.2)',
            border: isDraggingToDiscard ? '4px solid rgba(255, 0, 0, 0.9)' : '3px dashed rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 150,
            transition: 'all 0.2s ease',
            transform: isDraggingToDiscard ? 'scale(1.15)' : 'scale(1)',
            boxShadow: isDraggingToDiscard ? '0 0 40px rgba(255, 0, 0, 0.8), inset 0 0 20px rgba(255, 0, 0, 0.3)' : '0 0 15px rgba(0, 0, 0, 0.3)',
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingToDiscard(true);
            handleDragOver(e);
          }}
          onDragLeave={() => setIsDraggingToDiscard(false)}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedCardIndex !== null) {
              discardCard(draggedCardIndex);
            }
            // Reset all drag states
            setDraggedCard(null);
            setDraggedCardIndex(null);
            setIsDraggingToPlay(false);
            setIsDraggingToDiscard(false);
            setDragInitiated(false);
            setHoveredCardIndex(null);
          }}
        >
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
            gap: '5px'
          }}>
            <div style={{
              fontSize: 'clamp(28px, 6vw, 45px)',
              opacity: isDraggingToDiscard ? 1 : 0.7,
              filter: isDraggingToDiscard ? 'brightness(1.2)' : 'brightness(1)',
            }}>
              🗑️
        </div>
            <div style={{
              fontSize: isDraggingToDiscard ? 'clamp(10px, 2vw, 14px)' : 'clamp(9px, 1.8vw, 12px)',
              fontWeight: 'bold',
              color: 'white',
              textAlign: 'center',
              opacity: isDraggingToDiscard ? 1 : 0.8,
            }}>
              {isDraggingToDiscard ? 'Release to' : 'Drag here to'}
              <br />
              Discard
      </div>
          </div>
        </div>
      )}

      {/* Expanded Card View */}
      {(expandedCardIndex !== null || isClosingExpanded) && selectedCard && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            pointerEvents: 'auto',
          }}

        >
          {/* Backdrop */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(5px)',
              transition: 'opacity 0.3s ease',
              opacity: expandedCardIndex !== null && !isClosingExpanded ? 1 : 0,
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              closeExpandedCard();
            }}
          />
          
          {/* Animated Card */}
          <div
            style={{
              position: 'absolute',
              left: expandedCardPosition ? expandedCardPosition.left : '50%',
              top: expandedCardPosition ? expandedCardPosition.top : '50%',
              width: expandedCardPosition ? expandedCardPosition.width : 320,
              height: expandedCardPosition ? expandedCardPosition.height : Math.min(window.innerHeight * 0.85, 550),
              transform: expandedCardPosition ? 'none' : 'translate(-50%, -50%)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              ...(!expandedCardPosition && {
                left: '50%',
                top: '50%',
                width: 320,
                height: Math.min(window.innerHeight * 0.85, 550),
                transform: 'translate(-50%, -50%) scale(1)',
              }),
              pointerEvents: 'auto',
            }}
            onTouchStart={(e) => {
              const startY = e.touches[0].clientY;
              
              const handleTouchMove = (moveEvent) => {
                const currentY = moveEvent.touches[0].clientY;
                const deltaY = currentY - startY;
                
                if (deltaY > 50) { // Swipe down threshold
                  closeExpandedCard();
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                }
              };
              
              const handleTouchEnd = () => {
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };
              
              document.addEventListener('touchmove', handleTouchMove);
              document.addEventListener('touchend', handleTouchEnd);
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                padding: '10px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Card Image */}
              <img 
                src={selectedCard.imageUrl} 
                alt={selectedCard.name}
                style={{
                  width: '100%',
                  height: '50%',
                  minHeight: '180px',
                  maxHeight: '280px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  marginBottom: '10px',
                  flexShrink: 0,
                }}
              />
              
              {/* Card Details */}
      <div style={{
                padding: '10px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                overflowY: 'auto',
                minHeight: 0,
              }}>
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#000' }}>{selectedCard.name}</h2>
                  <p style={{ margin: 0, fontSize: '14px', color: '#333', lineHeight: '1.5', maxHeight: '120px', overflowY: 'auto' }}>
                    {selectedCard.description}
                  </p>
                </div>
                
                <div style={{ flex: 1, minHeight: '10px' }} />
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                  {selectedCard.prerequisites && selectedCard.prerequisites.length > 0 && (
                    <div style={{
                      backgroundColor: '#fff3e0',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      color: '#f57c00',
                    }}>
                      Requires: {selectedCard.prerequisites.join(', ')}
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                {selectedCardIsInHand && (
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginTop: '12px',
                    flexShrink: 0,
                  }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '16px 20px',
                        minHeight: '48px',
                        backgroundColor: (selectedCard && checkPrerequisites(selectedCard, playedCards)) ? '#4caf50' : '#ccc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: (selectedCard && checkPrerequisites(selectedCard, playedCards)) ? 'pointer' : 'not-allowed',
                        transition: 'all 0.3s ease',
                      }}
                      onClick={() => {
                        if (selectedCard && checkPrerequisites(selectedCard, playedCards) && selectedCardIndex !== null) {
                          playCard(selectedCardIndex);
                          closeExpandedCard();
                        }
                      }}
                      disabled={!(selectedCard && checkPrerequisites(selectedCard, playedCards))}
                    >
                      Play Card
                    </button>
                    <button
                      style={{
                        padding: '16px 30px',
                        minHeight: '48px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onClick={() => {
                        if (selectedCardIndex !== null) {
                          discardCard(selectedCardIndex);
                          closeExpandedCard();
                        }
                      }}
                    >
                      Discard
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CardDetailModal is replaced by the expanded card view above
      <CardDetailModal
        show={showCardModal}
        card={selectedCard}
        isInHand={selectedCardIsInHand}
        canPlay={selectedCardIsInHand && selectedCard && checkPrerequisites(selectedCard, playedCards)}
        onPlay={() => {
          if (selectedCardIndex !== null) {
            playCard(selectedCardIndex);
          }
        }}
        onDiscard={() => {
          if (selectedCardIndex !== null) {
            discardCard(selectedCardIndex);
          }
        }}
        onHide={() => {
          setShowCardModal(false);
          setSelectedCard(null);
          setSelectedCardIndex(null);
          setSelectedCardIsInHand(false);
        }}
        hasGameEnded={hasGameEnded()} // Add this prop
      />
      */}
      
      {/* Floating Dragged Card */}
      {draggedCard && draggedCardIndex !== null && !hasGameEnded() && (
        <div
          style={{
            position: 'fixed',
            left: dragPosition.x - 70,
            top: dragPosition.y - 100,
            width: '140px',
            height: '200px',
            pointerEvents: 'none',
            zIndex: 10000,
            transform: 'rotate(-5deg) scale(1.1)',
            transition: 'none',
          }}
        >
          <div style={{
            ...cardStyle,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderRadius: '10px',
            border: '2px solid rgba(0,0,0,0.08)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 60px rgba(255, 255, 255, 0.4)',
            cursor: 'grabbing',
            filter: 'brightness(1.2) drop-shadow(0 0 30px rgba(255, 255, 255, 0.8))',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <img 
              src={draggedCard.imageUrl} 
              alt={draggedCard.name} 
              style={{
                width: '100%', 
                height: '65%', 
                objectFit: 'cover',
                borderRadius: '10px 10px 0 0',
              }} 
            />
            <div style={{ 
              padding: '5px', 
              height: '35%', 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{draggedCard.name}</div>
              <div style={{ fontSize: '10px', lineHeight: '1.2' }}>{draggedCard.description}</div>
            </div>
          </div>
        </div>
      )}
      
<FactoryModal
        show={showFactoryModal}
        onHide={() => setShowFactoryModal(false)}
        numChoices={factoryChoices.length}
        onSelect={handleFactorySelection}
        availableCards={[
          availableCards.find(card => card.name === 'Wind Turbine'),
          availableCards.find(card => card.name === 'Solar Panel'),
          availableCards.find(card => card.name === 'Tools')
        ]}
      />
     <ScavengerModal
         visible={scavengerModalVisible}
         cards={discardPile}
         onSelect={(card) => {
           if (selectedScavengerCards.includes(card)) {
             setSelectedScavengerCards(prev => 
               prev.filter(c => c !== card)
             );
           } else if (selectedScavengerCards.length < 2) {
             setSelectedScavengerCards(prev => [...prev, card]);
           }
         }}
         onConfirm={() => handleScavengerSelection(selectedScavengerCards)}
         onCancel={() => {
           setSelectedScavengerCards([]);
           setScavengerModalVisible(false);
         }}
         selectedCards={selectedScavengerCards}
       />
     
     <RecycleModal
         visible={recycleModalVisible}
         discardPile={discardPile}
         onSelect={handleRecycleSelection}
         onConfirm={handleRecycleConfirm}
         onCancel={() => {
           setSelectedRecycleCards([]);
           setRecycleModalVisible(false);
         }}
         selectedCards={selectedRecycleCards}
       />

       {/* Energy Score Modal */}
       {showEnergyModal && (
         <div style={{
           position: 'fixed',
           top: 0,
           left: 0,
           right: 0,
           bottom: 0,
           backgroundColor: 'rgba(0, 0, 0, 0.85)',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           zIndex: 10000,
           backdropFilter: 'blur(10px)',
         }}
         onClick={() => setShowEnergyModal(false)}
         >
           <div 
             style={{
               background: 'linear-gradient(145deg, rgba(10, 10, 20, 0.98) 0%, rgba(20, 20, 40, 0.98) 100%)',
               border: '3px solid rgba(255, 215, 0, 0.6)',
               borderRadius: '25px',
               padding: '50px 40px',
               maxWidth: '550px',
               width: '90%',
               boxShadow: '0 30px 80px rgba(0, 0, 0, 0.7), 0 0 150px rgba(255, 215, 0, 0.3), inset 0 0 50px rgba(255, 215, 0, 0.1)',
               position: 'relative',
               overflow: 'hidden',
               animation: 'scaleIn 0.3s ease-out',
             }}
             onClick={(e) => e.stopPropagation()}
           >

             
             {/* Close button */}
             <button
               style={{
                 position: 'absolute',
                 top: '20px',
                 right: '20px',
                 background: 'none',
                 border: 'none',
                 color: 'rgba(255, 255, 255, 0.6)',
                 fontSize: '24px',
                 cursor: 'pointer',
                 transition: 'all 0.3s ease',
                 zIndex: 1,
               }}
               onClick={() => setShowEnergyModal(false)}
               onMouseEnter={(e) => e.target.style.color = 'rgba(255, 255, 255, 1)'}
               onMouseLeave={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.6)'}
             >
               ✕
             </button>
             
             {/* Decorative elements */}
             <div style={{
               position: 'absolute',
               top: '-50px',
               left: '-50px',
               width: '100px',
               height: '100px',
               background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)',
               borderRadius: '50%',
               filter: 'blur(30px)',
             }} />
             <div style={{
               position: 'absolute',
               bottom: '-30px',
               right: '-30px',
               width: '80px',
               height: '80px',
               background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
               borderRadius: '50%',
               filter: 'blur(25px)',
             }} />
             
             {/* Content */}
             <div style={{ position: 'relative', zIndex: 1 }}>
               <h2 style={{
                 margin: 0,
                 marginBottom: '35px',
                 fontSize: '32px',
                 fontWeight: 'bold',
                 color: '#ffd700',
                 textAlign: 'center',
                 textTransform: 'uppercase',
                 letterSpacing: '4px',
                 textShadow: '0 0 30px rgba(255, 215, 0, 0.7), 0 0 10px rgba(255, 215, 0, 0.9)',
               }}>
                 {hasGameEnded() ? 'Game Complete!' : 'Energy Score'}
               </h2>
               
               {/* Current Energy */}
               <div style={{
                 marginBottom: '30px',
                 padding: '25px',
                 background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 193, 7, 0.1) 100%)',
                 border: '2px solid rgba(255, 215, 0, 0.4)',
                 borderRadius: '20px',
                 textAlign: 'center',
                 position: 'relative',
                 overflow: 'hidden',
               }}>
                 <div style={{
                   position: 'absolute',
                   top: '0',
                   left: '0',
                   right: '0',
                   height: '1px',
                   background: 'linear-gradient(90deg, transparent 0%, rgba(255, 215, 0, 0.6) 50%, transparent 100%)',
                 }} />
                 <div style={{
                   fontSize: '16px',
                   color: 'rgba(255, 215, 0, 0.9)',
                   marginBottom: '10px',
                   textTransform: 'uppercase',
                   letterSpacing: '3px',
                   fontWeight: '500',
                 }}>
                   {hasGameEnded() ? 'Final Score' : 'Current Energy'}
                 </div>
                 <div style={{
                   fontSize: '56px',
                   fontWeight: 'bold',
                   color: '#ffd700',
                   textShadow: '0 0 30px rgba(255, 215, 0, 0.6), 0 4px 10px rgba(0, 0, 0, 0.5)',
                   lineHeight: '1',
                 }}>
                   {calculateEnergy()}
                 </div>
               </div>
               
               {/* Highest Score */}
               <div style={{
                 padding: '20px',
                 background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)',
                 border: '1px solid rgba(255, 215, 0, 0.25)',
                 borderRadius: '15px',
                 textAlign: 'center',
               }}>
                 <div style={{
                   fontSize: '14px',
                   color: 'rgba(255, 255, 255, 0.7)',
                   marginBottom: '8px',
                   textTransform: 'uppercase',
                   letterSpacing: '2px',
                 }}>
                   Best Score
                 </div>
                 <div style={{
                   fontSize: '40px',
                   fontWeight: 'bold',
                   color: hasGameEnded() && calculateEnergy() > calculateScore() ? '#ff6b6b' : '#fff',
                   textShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
                   textDecoration: hasGameEnded() && calculateEnergy() > calculateScore() ? 'line-through' : 'none',
                   opacity: hasGameEnded() && calculateEnergy() > calculateScore() ? 0.6 : 1,
                   transition: 'all 0.5s ease',
                 }}>
                   {calculateScore()}
                 </div>
               </div>
               
               {hasGameEnded() && calculateEnergy() > calculateScore() && (
                 <div style={{
                   marginTop: '25px',
                   padding: '20px',
                   background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.2) 0%, rgba(255, 193, 7, 0.2) 100%)',
                   border: '2px solid rgba(255, 193, 7, 0.5)',
                   borderRadius: '15px',
                   textAlign: 'center',
                   fontSize: '20px',
                   color: '#ffd700',
                   fontWeight: 'bold',
                   textTransform: 'uppercase',
                   letterSpacing: '2px',
                   textShadow: '0 0 20px rgba(255, 215, 0, 0.6)',
                   animation: 'pulse 1.5s ease-in-out infinite',
                 }}>
                   🏆 New High Score! 🏆
                 </div>
               )}
               
               {/* Game End Actions */}
               {hasGameEnded() && (
                 <div style={{
                   marginTop: '30px',
                   display: 'flex',
                   gap: '15px',
                   justifyContent: 'center',
                   flexWrap: 'wrap',
                 }}>
                   <button
                     onClick={() => window.location.reload()}
                     style={{
                       padding: '12px 30px',
                       fontSize: '16px',
                       fontWeight: 'bold',
                       color: '#000',
                       backgroundColor: '#ffd700',
                       border: 'none',
                       borderRadius: '25px',
                       cursor: 'pointer',
                       transition: 'all 0.3s ease',
                       textTransform: 'uppercase',
                       letterSpacing: '1px',
                       boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.transform = 'scale(1.05)';
                       e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.transform = 'scale(1)';
                       e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
                     }}
                   >
                     Play Again
                   </button>
                   <GameCenterButtons leaderboardID="allTimeHighScore" />
                 </div>
               )}
             </div>
           </div>
         </div>
       )}
       
         <CardSelectionModal
           visible={showCardSelectionModal}
           cards={deck}
           onSelect={handleFuturistSelection}
           onCancel={() => setShowCardSelectionModal(false)}
         />
         
           <ThrivingSpiritModal
             visible={thrivingSpiritModalVisible}
             discardPile={discardPile}
             deck={deck}
             onSelect={handleThrivingSpiritSelection}
             onConfirm={handleThrivingSpiritConfirm}
             onCancel={() => {
               setSelectedThrivingSpiritDiscardCards([]);
               setSelectedThrivingSpiritDeckCards([]);
               setThrivingSpiritModalVisible(false);
             }}
             selectedDiscardCards={selectedThrivingSpiritDiscardCards}
             selectedDeckCards={selectedThrivingSpiritDeckCards}
           />
           <LibraryModal
             visible={libraryModalVisible}
             deck={deck}
             onSelect={handleLibrarySelection}
             onConfirm={handleLibraryConfirm}
             onCancel={() => {
               setSelectedLibraryCards([]);
               setLibraryModalVisible(false);
             }}
             selectedCards={selectedLibraryCards}
             numSelectionsAllowed={calculateLibrarySelections()}
           />
         </div>
    </div>
       );
     };
     
     // Update the GameCenterButtons to only show leaderboard
     const GameCenterButtons = ({ leaderboardID }) => {
       return (
         <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
           <LeaderboardButton leaderboardID={leaderboardID} />
         </div>
       );
     };
     
     const LeaderboardButton = ({ leaderboardID }) => {
       const [isLoading, setIsLoading] = useState(false);
       const [isWeb] = useState(typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform());
       
       const handleClick = async () => {
         if (isWeb) {
           // Show coming soon message on web
           alert('Leaderboard coming soon for web version! Download the app for full features.');
           return;
         }
         
         setIsLoading(true);
         try {
           // Attempt to authenticate first (required on iOS)
           await CapacitorGameConnect.authenticate()
             .catch(authError => {
               console.warn('Authentication warning (may be normal on Android):', authError);
               // Continue anyway as Android may not need authentication
             });
           
           // Then show the leaderboard
           await CapacitorGameConnect.showLeaderboard({ leaderboardID });
         } catch (error) {
           console.error('Error showing leaderboard:', error);
           alert('Could not open leaderboard. Please make sure you are signed in to Game Center.');
         } finally {
           setIsLoading(false);
         }
       };
     
       return (
         <button 
           onClick={handleClick} 
           disabled={isLoading}
           style={{
             marginTop: '10px',
             height: '40px',
             color: 'black',
             backgroundColor: isWeb ? '#ddd' : 'white',
             borderRadius: '10px',
             fontFamily: 'Orbitron',
             marginLeft: '10px',
             cursor: isLoading ? 'wait' : 'pointer',
             opacity: isWeb ? 0.7 : (isLoading ? 0.7 : 1),
             fontSize: '14px',
             padding: '0 15px'
           }}
         >
           {isLoading ? 'Opening...' : (isWeb ? 'Leaderboard (Coming Soon)' : 'Leaderboard')}
         </button>
       );
     };
     
     const ScavengerModal = ({ visible, cards, selectedCards, onSelect, onConfirm, onCancel }) => {
       // Process card array only once when props change
       const [processedCards, setProcessedCards] = useState([]);
       
       // Update processed cards when props change
       useEffect(() => {
         if (visible) {
           // Add stable uniqueIds to cards if they don't have them
           setProcessedCards(cards.map((card, index) => ({
             ...card,
             uniqueId: card.uniqueId || card.id || `scavenger-${card.name}-${index}-${Date.now()}`
           })));
         }
       }, [visible, cards]);

       const isCardSelected = (card) => {
         return selectedCards.some(selectedCard => 
           selectedCard.uniqueId === card.uniqueId || selectedCard.id === card.id
         );
       };

      if (!visible) return null;

       return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
             display: 'flex',
          alignItems: 'center',
             justifyContent: 'center',
             padding: '20px',
        }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
            border: '2px solid rgba(147, 51, 234, 0.5)',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '1000px',
            height: '90vh',
            maxHeight: '700px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(147, 51, 234, 0.2)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 30px',
              borderBottom: '1px solid rgba(147, 51, 234, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{
                margin: 0,
                color: '#9333ea',
                fontSize: '24px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 0 10px rgba(147, 51, 234, 0.5)',
              }}>
                Scavenger - Select up to 2 cards from discard pile
              </h2>
            </div>
            
            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '30px',
            }}>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h3 style={{
                  color: '#87ceeb',
                  fontSize: '18px',
                  marginBottom: '10px',
                }}>
                  Selected Cards: {selectedCards.length}/2
                </h3>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '20px',
                maxWidth: '450px',
                margin: '0 auto',
           }}>
             {processedCards.map((card, index) => (
               <div
                 key={card.uniqueId || index}
                 onClick={() => onSelect(card)}
                 style={{
                   ...cardStyle,
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '3/4',
                      border: isCardSelected(card) ? '3px solid #9333ea' : '2px solid transparent',
                   cursor: 'pointer',
                   opacity: selectedCards.length >= 2 && !isCardSelected(card) ? 0.6 : 1,
                      transform: isCardSelected(card) ? 'scale(1.05) translateY(-10px)' : 'scale(1)',
                      transition: 'all 0.3s ease',
                      boxShadow: isCardSelected(card) 
                        ? '0 8px 30px rgba(147, 51, 234, 0.4)' 
                        : '0 4px 15px rgba(0, 0, 0, 0.4)',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCards.length < 2 || isCardSelected(card)) {
                        e.currentTarget.style.transform = isCardSelected(card) 
                          ? 'scale(1.05) translateY(-10px)' 
                          : 'scale(1.05) translateY(-10px)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(147, 51, 234, 0.4)';
                        e.currentTarget.style.zIndex = '10';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = isCardSelected(card) 
                        ? 'scale(1.05) translateY(-10px)' 
                        : 'scale(1)';
                      e.currentTarget.style.boxShadow = isCardSelected(card)
                        ? '0 8px 30px rgba(147, 51, 234, 0.4)'
                        : '0 4px 15px rgba(0, 0, 0, 0.4)';
                      e.currentTarget.style.zIndex = '1';
                    }}
                  >
                    <img 
                      src={card.imageUrl} 
                      alt={card.name}
                      style={{
                        width: '100%',
                        height: '55%',
                        objectFit: 'cover',
                        borderRadius: '10px 10px 0 0',
                      }}
                    />
                    <div style={{
                      padding: '8px',
                      height: '45%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      gap: '4px',
                    }}>
                      <div style={{ 
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#000',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {card.name}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#333',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.2',
                      }}>
                        {card.description}
                      </div>
                 </div>
               </div>
             ))}
              </div>
            </div>
            
            {/* Fixed Footer */}
            <div style={{
              padding: '20px 30px',
              borderTop: '1px solid rgba(147, 51, 234, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}>
              
              <button
                   onClick={onConfirm}
                   disabled={selectedCards.length === 0}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: selectedCards.length === 0 ? '#666' : '#000',
                  backgroundColor: selectedCards.length === 0 ? 'rgba(147, 51, 234, 0.3)' : '#9333ea',
                  border: '2px solid transparent',
                  borderRadius: '30px',
                  cursor: selectedCards.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  opacity: selectedCards.length === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (selectedCards.length > 0) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(147, 51, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                 >
                   Add to Hand ({selectedCards.length} cards)
              </button>
               </div>
             </div>
        </div>
       );
     };
     
     const RecycleModal = ({
       visible,
       discardPile,
       onSelect,
       onConfirm,
       onCancel,
       selectedCards
     }) => {
       // Process card array only once when props change
       const [processedCards, setProcessedCards] = useState([]);
       
       // Update processed cards when props change
       useEffect(() => {
         if (visible) {
           // Filter for recyclable cards
           const recyclableCards = discardPile.filter(card => 
             ['Solar Panel', 'Wind Turbine', 'Tools', 'Battery'].includes(card.name)
           );
           
           // Add stable uniqueIds to cards if they don't have them
           setProcessedCards(recyclableCards.map((card, index) => ({
             ...card,
             uniqueId: card.uniqueId || card.id || `recycle-${card.name}-${index}-${Date.now()}`
           })));
         }
       }, [visible, discardPile]);

       const isCardSelected = (card) => {
         return selectedCards.some(selectedCard => 
           selectedCard.uniqueId === card.uniqueId || selectedCard.id === card.id
         );
       };

             if (!visible) return null;

       return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
             backdropFilter: 'blur(10px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
             padding: '20px',
           }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
            border: '2px solid rgba(0, 255, 127, 0.5)',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '1000px',
            height: '90vh',
            maxHeight: '700px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 255, 127, 0.2)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 30px',
              borderBottom: '1px solid rgba(0, 255, 127, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{
                margin: 0,
                color: '#00ff7f',
                fontSize: '24px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 0 10px rgba(0, 255, 127, 0.5)',
              }}>
                Recycler - Select Cards to Recycle
              </h2>
            </div>
            
            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '30px',
            }}>
                           <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                <h3 style={{
                  color: '#87ceeb',
                  fontSize: '18px',
                  marginBottom: '10px',
                }}>
                  Select technology cards to recycle
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '14px',
                  margin: 0,
                }}>
                 You'll receive one Materials card for each card recycled
                </p>
             </div>
    
             {processedCards.length > 0 ? (
               <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                  maxWidth: '450px',
                  margin: '0 auto 30px auto',
               }}>
                 {processedCards.map((card, index) => (
                   <div
                     key={card.uniqueId || index}
                     onClick={() => onSelect(card)}
                     style={{
                       ...cardStyle,
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '2.5/4',
                        border: isCardSelected(card) ? '3px solid #00ff7f' : '2px solid transparent',
                       cursor: 'pointer',
                        transform: isCardSelected(card) ? 'scale(1.05) translateY(-10px)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                        boxShadow: isCardSelected(card) 
                          ? '0 8px 30px rgba(0, 255, 127, 0.4)' 
                          : '0 4px 15px rgba(0, 0, 0, 0.4)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = isCardSelected(card) 
                          ? 'scale(1.05) translateY(-10px)' 
                          : 'scale(1.05) translateY(-10px)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 255, 127, 0.4)';
                        e.currentTarget.style.zIndex = '10';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = isCardSelected(card) 
                          ? 'scale(1.05) translateY(-10px)' 
                          : 'scale(1)';
                        e.currentTarget.style.boxShadow = isCardSelected(card)
                          ? '0 8px 30px rgba(0, 255, 127, 0.4)'
                          : '0 4px 15px rgba(0, 0, 0, 0.4)';
                        e.currentTarget.style.zIndex = '1';
                      }}
                    >
                       {isCardSelected(card) && (
                         <div style={{
                           position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          background: '#00ff7f',
                          color: '#000',
                           borderRadius: '50%',
                          width: '30px',
                          height: '30px',
                           display: 'flex',
                           alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          border: '2px solid #fff',
                          zIndex: 20,
                         }}>
                           ✓
                         </div>
                       )}
                      <img 
                        src={card.imageUrl} 
                        alt={card.name}
                        style={{
                          width: '100%',
                          height: '55%',
                          objectFit: 'cover',
                          borderRadius: '10px 10px 0 0',
                        }}
                      />
                      <div style={{
                        padding: '8px',
                        height: '45%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        gap: '4px',
                      }}>
                        <div style={{ 
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#000',
                          lineHeight: '1.3',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {card.name}
                     </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#333',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.3',
                        }}>
                          {card.description}
                        </div>
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
                <div style={{
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '16px',
                  padding: '60px',
                }}>
                 No recyclable cards in discard pile
               </div>
             )}
    
                           {/* Materials Preview */}
              <div style={{
                background: 'rgba(0, 255, 127, 0.1)',
                border: '1px solid rgba(0, 255, 127, 0.3)',
                borderRadius: '15px',
                padding: '20px',
                textAlign: 'center',
              }}>
                <h4 style={{
                  color: '#00ff7f',
                  fontSize: '16px',
                  marginBottom: '15px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  You will receive:
                </h4>
               {selectedCards.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                  }}>
                   <img 
                     src={materialsImg} 
                     alt="Materials" 
                      style={{ 
                        height: '60px', 
                        width: '60px', 
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 0 10px rgba(0, 255, 127, 0.3))',
                      }}
                    />
                    <span style={{
                      fontSize: '28px',
                      fontWeight: 'bold',
                      color: '#00ff7f',
                      textShadow: '0 0 10px rgba(0, 255, 127, 0.5)',
                    }}>
                      × {selectedCards.length}
                    </span>
                 </div>
               ) : (
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: '14px',
                  }}>
                    Select cards to recycle
                  </div>
                )}
              </div>
             </div>
    
            {/* Fixed Footer */}
            <div style={{
              padding: '20px 30px',
              borderTop: '1px solid rgba(0, 255, 127, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}>
              
              <button
                 onClick={onConfirm}
                 disabled={selectedCards.length === 0}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: selectedCards.length === 0 ? '#666' : '#000',
                  backgroundColor: selectedCards.length === 0 ? 'rgba(0, 255, 127, 0.3)' : '#00ff7f',
                  border: '2px solid transparent',
                  borderRadius: '30px',
                  cursor: selectedCards.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  opacity: selectedCards.length === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (selectedCards.length > 0) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 255, 127, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Recycle ({selectedCards.length} cards)
              </button>
             </div>
          </div>
        </div>
       );
     };
     
     const CardSelectionModal = ({ visible, cards, onSelect, onCancel }) => {
       // Process card array only once when props change
       const [processedCards, setProcessedCards] = useState([]);
       
       // Update processed cards when props change
       useEffect(() => {
         if (visible) {
           // Add unique IDs only for the display, original IDs will be preserved for deck management
           setProcessedCards(cards.map((card, index) => ({
             ...card,
             displayId: `selection-${card.name}-${index}-${Date.now()}`
           })));
         }
       }, [visible, cards]);
      
      if (!visible) return null;
       
       return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
             display: 'flex',
          alignItems: 'center',
             justifyContent: 'center',
             padding: '20px',
        }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
            border: '2px solid rgba(135, 206, 235, 0.5)',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '1000px',
            height: '90vh',
            maxHeight: '700px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(135, 206, 235, 0.2)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 30px',
              borderBottom: '1px solid rgba(135, 206, 235, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{
                margin: 0,
                color: '#87ceeb',
                fontSize: '24px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 0 10px rgba(135, 206, 235, 0.5)',
              }}>
                Select a card from your deck
              </h2>
            </div>
            
            {/* Cards Grid */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '30px',
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '20px',
                maxWidth: '450px',
                margin: '0 auto',
           }}>
             {processedCards.map((card) => (
               <div
                 key={card.displayId || card.id}
                 onClick={() => onSelect(card)}
                 style={{
                   ...cardStyle,
                   width: '100%',
                   height: 'auto',
                   aspectRatio: '2.5/4',
                   cursor: 'pointer',
                    transform: 'scale(1)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05) translateY(-10px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(135, 206, 235, 0.4)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.4)';
                    e.currentTarget.style.zIndex = '1';
                  }}
                >
                  <img 
                    src={card.imageUrl} 
                    alt={card.name}
                    style={{
                      width: '100%',
                      height: '65%',
                      objectFit: 'cover',
                      borderRadius: '10px 10px 0 0',
                    }}
                  />
                  <div style={{
                    padding: '10px',
                    height: '35%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ 
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#000',
                      marginBottom: '5px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {card.name}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: '#333',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.3',
                    }}>
                      {card.description}
                    </div>
                 </div>
               </div>
             ))}
              </div>
            </div>
          </div>
        </div>
       );
     };
     
     const FactoryModal = ({ show, onHide, numChoices, onSelect, availableCards }) => {
       const [selectedCards, setSelectedCards] = useState([]);
     
       const handleCardSelect = (card) => {
         if (selectedCards.length < numChoices) {
           setSelectedCards([...selectedCards, card]);
         }
       };
     
       const handleRemoveCard = (index) => {
         setSelectedCards(selectedCards.filter((_, i) => i !== index));
       };
     
       const handleConfirm = () => {
         onSelect(selectedCards);
         setSelectedCards([]);
       };
     
       // Reset selections when modal closes
       useEffect(() => {
         if (!show) {
           setSelectedCards([]);
         }
       }, [show]);
    
      if (!show) return null;
     
       return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}>
          <div style={{
            background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
            border: '2px solid rgba(255, 193, 7, 0.5)',
            borderRadius: '20px',
            width: '90%',
            maxWidth: '1200px',
            height: '90vh',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(255, 193, 7, 0.2)',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 30px',
              borderBottom: '1px solid rgba(255, 193, 7, 0.3)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{
                margin: 0,
                color: '#ffc107',
                fontSize: '24px',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                textShadow: '0 0 10px rgba(255, 193, 7, 0.5)',
              }}>
                Factory - Select {numChoices} cards to add to your deck
              </h2>
            </div>
            
            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '30px',
            }}>
              {/* Available Cards */}
              <div style={{ marginBottom: '40px' }}>
                <h3 style={{
                  color: '#87ceeb',
                  fontSize: '18px',
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  Available Cards
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                  maxWidth: '450px',
                  margin: '0 auto',
                }}>
                 {availableCards.map((card, index) => (
                   <div
                     key={index}
                     onClick={() => handleCardSelect(card)}
                     style={{
                       ...cardStyle,
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '2.5/4',
                       cursor: selectedCards.length < numChoices ? 'pointer' : 'not-allowed',
                        opacity: selectedCards.length < numChoices ? 1 : 0.6,
                        transform: 'scale(1)',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCards.length < numChoices) {
                          e.currentTarget.style.transform = 'scale(1.05) translateY(-10px)';
                          e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 193, 7, 0.4)';
                          e.currentTarget.style.zIndex = '10';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.4)';
                        e.currentTarget.style.zIndex = '1';
                     }}
                   >
                     <img 
                       src={card.imageUrl} 
                       alt={card.name} 
                        style={{
                          width: '100%',
                          height: '65%',
                          objectFit: 'cover',
                          borderRadius: '10px 10px 0 0',
                        }}
                      />
                      <div style={{
                        padding: '10px',
                        height: '35%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}>
                        <div style={{ 
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#000',
                          marginBottom: '5px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {card.name}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: '#333',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.3',
                        }}>
                          {card.description}
                        </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
      
              {/* Selected Cards */}
             <div>
                <h3 style={{
                  color: '#87ceeb',
                  fontSize: '18px',
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}>
                  Selected Cards ({selectedCards.length}/{numChoices})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '15px',
                  maxWidth: '450px',
                  margin: '0 auto',
                }}>
                 {selectedCards.map((card, index) => (
                   <div
                     key={index}
                     style={{
                       ...cardStyle,
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '2.5/4',
                        position: 'relative',
                        boxShadow: '0 4px 15px rgba(0, 255, 127, 0.3)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                     }}
                   >
                     <button
                       onClick={() => handleRemoveCard(index)}
                       style={{ 
                          position: 'absolute',
                          top: '-10px',
                          right: '-10px',
                          width: '30px',
                          height: '30px',
                          borderRadius: '50%',
                          backgroundColor: '#ff4444',
                          border: '2px solid #fff',
                          color: '#fff',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10,
                          transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.backgroundColor = '#ff6666';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.backgroundColor = '#ff4444';
                       }}
                     >
                       ×
                     </button>
                     <img 
                       src={card.imageUrl} 
                       alt={card.name} 
                        style={{
                          width: '100%',
                          height: '60%',
                          objectFit: 'cover',
                          borderRadius: '10px 10px 0 0',
                        }}
                      />
                      <div style={{
                        padding: '6px',
                        height: '40%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <div style={{ 
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: '#000',
                          textAlign: 'center',
                          lineHeight: '1.2',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {card.name}
                        </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
            </div>
            
            {/* Fixed Footer */}
            <div style={{
              padding: '20px 30px',
              borderTop: '1px solid rgba(255, 193, 7, 0.3)',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            }}>
              <button
                onClick={onHide}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
               Cancel
              </button>
              <button
               onClick={handleConfirm}
               disabled={selectedCards.length !== numChoices}
                style={{
                  padding: '12px 30px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: selectedCards.length !== numChoices ? '#666' : '#000',
                  backgroundColor: selectedCards.length !== numChoices ? 'rgba(255, 193, 7, 0.3)' : '#ffc107',
                  border: '2px solid transparent',
                  borderRadius: '30px',
                  cursor: selectedCards.length !== numChoices ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  opacity: selectedCards.length !== numChoices ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (selectedCards.length === numChoices) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 193, 7, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
             >
               Add to Deck
              </button>
            </div>
          </div>
        </div>
       );
     };
     
    const ThrivingSpiritModal = ({ 
      visible, 
      discardPile,
      deck,
      onSelect, 
      onConfirm, 
      onCancel, 
      selectedDiscardCards,
      selectedDeckCards
    }) => {
      // Process card arrays only once when props change
      const [processedDiscardPile, setProcessedDiscardPile] = useState([]);
      const [processedDeck, setProcessedDeck] = useState([]);
      
      // Update processed cards when props change
      useEffect(() => {
        if (visible) {
          // Add stable uniqueIds to cards if they don't have them
          setProcessedDiscardPile(discardPile.map((card, index) => ({
            ...card,
            uniqueId: card.uniqueId || card.id || `discard-${card.name}-${index}-${Date.now()}`
          })));
          
          setProcessedDeck(deck.map((card, index) => ({
            ...card,
            uniqueId: card.uniqueId || card.id || `deck-${card.name}-${index}-${Date.now()}`
          })));
        }
      }, [visible, discardPile, deck]);

      const isCardSelected = (card, selectedCards) => {
        return selectedCards.some(selectedCard => selectedCard.uniqueId === card.uniqueId);
      };

      const renderCard = (card, source, selectedCards, maxSelected) => (
        <div
          key={card.uniqueId}
          onClick={() => onSelect(source, card)}
          style={{
            ...cardStyle,
          width: '160px',
          height: 'auto',
          aspectRatio: '2.5/4',
          border: isCardSelected(card, selectedCards) ? '3px solid #00ff7f' : '2px solid transparent',
            cursor: 'pointer',
            opacity: selectedCards.length >= maxSelected && !isCardSelected(card, selectedCards) ? 0.6 : 1,
           transform: isCardSelected(card, selectedCards) ? 'scale(1.05) translateY(-10px)' : 'scale(1)',
           transition: 'all 0.3s ease',
           boxShadow: isCardSelected(card, selectedCards) 
             ? '0 8px 30px rgba(0, 255, 127, 0.4)' 
             : '0 4px 15px rgba(0, 0, 0, 0.4)',
           backgroundColor: 'rgba(255, 255, 255, 0.95)',
         }}
       >
         <img 
           src={card.imageUrl} 
           alt={card.name}
           style={{
             width: '100%',
             height: '65%',
             objectFit: 'cover',
             borderRadius: '8px 8px 0 0',
           }}
         />
         <div style={{
           padding: '8px',
           height: '35%',
           display: 'flex',
           flexDirection: 'column',
           justifyContent: 'space-between',
         }}>
           <div style={{ 
             fontSize: '12px',
             fontWeight: 'bold',
             color: '#000',
             marginBottom: '3px',
             whiteSpace: 'nowrap',
             overflow: 'hidden',
             textOverflow: 'ellipsis',
           }}>
             {card.name}
           </div>
           <div style={{
             fontSize: '10px',
             color: '#333',
             overflow: 'hidden',
             display: '-webkit-box',
             WebkitLineClamp: 2,
             WebkitBoxOrient: 'vertical',
             lineHeight: '1.2',
           }}>
             {card.description}
           </div>
          </div>
        </div>
      );
  
     if (!visible) return null;
   
      return (
       <div style={{
         position: 'fixed',
         top: 0,
         left: 0,
         right: 0,
         bottom: 0,
         backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
         zIndex: 10000,
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
            padding: '20px',
       }}>
         <div style={{
           background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
           border: '2px solid rgba(0, 255, 127, 0.5)',
           borderRadius: '20px',
           width: '90%',
           maxWidth: '1200px',
           height: '90vh',
           maxHeight: '800px',
           display: 'flex',
           flexDirection: 'column',
           boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 255, 127, 0.2)',
           overflow: 'hidden',
         }}>
           {/* Header */}
           <div style={{
             padding: '20px 30px',
             borderBottom: '1px solid rgba(0, 255, 127, 0.3)',
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
           }}>
             <h2 style={{
               margin: 0,
               color: '#00ff7f',
               fontSize: '24px',
               fontWeight: 'bold',
               textTransform: 'uppercase',
               letterSpacing: '2px',
               textShadow: '0 0 10px rgba(0, 255, 127, 0.5)',
             }}>
               Select Cards - Thriving Spirit
                         </h2>
           </div>
           
           {/* Content */}
           <div style={{
             flex: 1,
             overflowY: 'auto',
             padding: '30px',
          }}>
            {/* Discard Pile Selection */}
             <div style={{ marginBottom: '40px' }}>
               <h3 style={{
                 color: '#87ceeb',
                 fontSize: '18px',
                 marginBottom: '20px',
                 textTransform: 'uppercase',
                 letterSpacing: '1px',
               }}>
                 Select up to 2 cards from discard pile ({selectedDiscardCards.length}/2)
               </h3>
              {processedDiscardPile.length > 0 ? (
                 <div style={{ 
                   display: 'grid',
                   gridTemplateColumns: 'repeat(2, 1fr)',
                   gap: '20px',
                   maxWidth: '450px',
                   margin: '0 auto',
                 }}>
                  {processedDiscardPile.map(card => renderCard(card, 'discard', selectedDiscardCards, 2))}
                </div>
              ) : (
                 <div style={{
                   textAlign: 'center',
                   color: 'rgba(255, 255, 255, 0.5)',
                   fontSize: '16px',
                   padding: '40px',
                 }}>
                   No cards in discard pile
                 </div>
              )}
            </div>
     
            {/* Deck Selection */}
             <div>
               <h3 style={{
                 color: '#87ceeb',
                 fontSize: '18px',
                 marginBottom: '20px',
                 textTransform: 'uppercase',
                 letterSpacing: '1px',
               }}>
                 Select up to 2 cards from deck ({selectedDeckCards.length}/2)
               </h3>
              {processedDeck.length > 0 ? (
                 <div style={{ 
                   display: 'grid',
                   gridTemplateColumns: 'repeat(2, 1fr)',
                   gap: '20px',
                   maxWidth: '450px',
                   margin: '0 auto',
                 }}>
                  {processedDeck.map(card => renderCard(card, 'deck', selectedDeckCards, 2))}
                </div>
              ) : (
                 <div style={{
                   textAlign: 'center',
                   color: 'rgba(255, 255, 255, 0.5)',
                   fontSize: '16px',
                   padding: '40px',
                 }}>
                   No cards in deck
                 </div>
               )}
             </div>
            </div>
     
           {/* Fixed Footer */}
           <div style={{
             padding: '20px 30px',
             borderTop: '1px solid rgba(0, 255, 127, 0.3)',
             display: 'flex',
             justifyContent: 'center',
             backgroundColor: 'rgba(0, 0, 0, 0.3)',
           }}>
             
             <button
                onClick={onConfirm}
                disabled={selectedDiscardCards.length === 0 && selectedDeckCards.length === 0}
               style={{
                 padding: '12px 30px',
                 fontSize: '16px',
                 fontWeight: 'bold',
                 color: selectedDiscardCards.length === 0 && selectedDeckCards.length === 0 ? '#666' : '#000',
                 backgroundColor: selectedDiscardCards.length === 0 && selectedDeckCards.length === 0 
                   ? 'rgba(0, 255, 127, 0.3)' 
                   : '#00ff7f',
                 border: '2px solid transparent',
                 borderRadius: '30px',
                 cursor: selectedDiscardCards.length === 0 && selectedDeckCards.length === 0 ? 'not-allowed' : 'pointer',
                 transition: 'all 0.3s ease',
                 textTransform: 'uppercase',
                 letterSpacing: '1px',
                 opacity: selectedDiscardCards.length === 0 && selectedDeckCards.length === 0 ? 0.5 : 1,
               }}
               onMouseEnter={(e) => {
                 if (selectedDiscardCards.length > 0 || selectedDeckCards.length > 0) {
                   e.currentTarget.style.transform = 'scale(1.05)';
                   e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 255, 127, 0.5)';
                 }
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.transform = 'scale(1)';
                 e.currentTarget.style.boxShadow = 'none';
               }}
              >
                Add Selected Cards ({selectedDiscardCards.length + selectedDeckCards.length})
             </button>
            </div>
         </div>
       </div>
      );
    };
     
    const LibraryModal = ({ 
      visible, 
      deck,
      onSelect, 
      onConfirm, 
      onCancel, 
      selectedCards,
      numSelectionsAllowed
    }) => {
      // Filter for only wisdom cards
      const wisdomCards = deck.filter(card => card.type === 'Wisdom').map((card, index) => ({
        ...card,
        uniqueId: card.uniqueId || `deck-${card.name}-${index}`
      }));
    
      const isCardSelected = (card) => {
        return selectedCards.some(selectedCard => selectedCard.uniqueId === card.uniqueId);
      };
   
     if (!visible) return null;
    
      return (
       <div style={{
         position: 'fixed',
         top: 0,
         left: 0,
         right: 0,
         bottom: 0,
         backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
         zIndex: 10000,
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
            padding: '20px',
          }}>
         <div style={{
           background: 'linear-gradient(145deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
           border: '2px solid rgba(100, 149, 237, 0.5)',
           borderRadius: '20px',
           width: '90%',
           maxWidth: '1000px',
           height: '90vh',
           maxHeight: '700px',
           display: 'flex',
           flexDirection: 'column',
           boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 100px rgba(100, 149, 237, 0.2)',
           overflow: 'hidden',
         }}>
           {/* Header */}
           <div style={{
             padding: '20px 30px',
             borderBottom: '1px solid rgba(100, 149, 237, 0.3)',
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
           }}>
             <h2 style={{
               margin: 0,
               color: '#6495ed',
               fontSize: '24px',
               fontWeight: 'bold',
               textTransform: 'uppercase',
               letterSpacing: '2px',
               textShadow: '0 0 10px rgba(100, 149, 237, 0.5)',
             }}>
               Library - Select Wisdom Cards
                         </h2>
           </div>
           
           {/* Content */}
           <div style={{
             flex: 1,
             overflowY: 'auto',
             padding: '30px',
           }}>
             <div style={{ marginBottom: '30px', textAlign: 'center' }}>
               <h3 style={{
                 color: '#87ceeb',
                 fontSize: '18px',
                 marginBottom: '10px',
               }}>
                 Select up to {numSelectionsAllowed} wisdom card{numSelectionsAllowed > 1 ? 's' : ''}
               </h3>
               <p style={{
                 color: 'rgba(255, 255, 255, 0.6)',
                 fontSize: '14px',
                 margin: 0,
               }}>
                Bonus selections available: {numSelectionsAllowed - 1} (from Curiosity cards played)
               </p>
            </div>
    
            {wisdomCards.length > 0 ? (
              <div style={{ 
                 display: 'grid',
                 gridTemplateColumns: 'repeat(2, 1fr)',
                 gap: '20px',
                 maxWidth: '450px',
                 margin: '0 auto',
              }}>
                {wisdomCards.map((card) => (
                  <div
                    key={card.uniqueId}
                    onClick={() => onSelect(card)}
                    style={{
                      ...cardStyle,
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '2.5/4',
                      border: isCardSelected(card) ? '3px solid #6495ed' : '2px solid transparent',
                      cursor: 'pointer',
                      opacity: selectedCards.length >= numSelectionsAllowed && !isCardSelected(card) ? 0.6 : 1,
                       transform: isCardSelected(card) ? 'scale(1.05) translateY(-10px)' : 'scale(1)',
                       transition: 'all 0.3s ease',
                       boxShadow: isCardSelected(card) 
                         ? '0 8px 30px rgba(100, 149, 237, 0.4)' 
                         : '0 4px 15px rgba(0, 0, 0, 0.4)',
                       backgroundColor: 'rgba(255, 255, 255, 0.95)',
                     }}
                     onMouseEnter={(e) => {
                       if (selectedCards.length < numSelectionsAllowed || isCardSelected(card)) {
                         e.currentTarget.style.transform = isCardSelected(card) 
                           ? 'scale(1.05) translateY(-10px)' 
                           : 'scale(1.05) translateY(-10px)';
                         e.currentTarget.style.boxShadow = '0 8px 30px rgba(100, 149, 237, 0.4)';
                       }
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.transform = isCardSelected(card) 
                         ? 'scale(1.05) translateY(-10px)' 
                         : 'scale(1)';
                       e.currentTarget.style.boxShadow = isCardSelected(card)
                         ? '0 8px 30px rgba(100, 149, 237, 0.4)'
                         : '0 4px 15px rgba(0, 0, 0, 0.4)';
                     }}
                   >
                     <img 
                       src={card.imageUrl} 
                       alt={card.name}
                       style={{
                         width: '100%',
                         height: '65%',
                         objectFit: 'cover',
                         borderRadius: '10px 10px 0 0',
                       }}
                     />
                     <div style={{
                       padding: '10px',
                       height: '35%',
                       display: 'flex',
                       flexDirection: 'column',
                       justifyContent: 'space-between',
                     }}>
                       <div style={{ 
                         fontSize: '14px',
                         fontWeight: 'bold',
                         color: '#000',
                         marginBottom: '5px',
                         whiteSpace: 'nowrap',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                       }}>
                         {card.name}
                       </div>
                       <div style={{
                         fontSize: '11px',
                         color: '#333',
                         overflow: 'hidden',
                         display: '-webkit-box',
                         WebkitLineClamp: 2,
                         WebkitBoxOrient: 'vertical',
                         lineHeight: '1.3',
                       }}>
                         {card.description}
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
               <div style={{
                 textAlign: 'center',
                 color: 'rgba(255, 255, 255, 0.5)',
                 fontSize: '16px',
                 padding: '60px',
               }}>
                No wisdom cards available in deck
              </div>
            )}
           </div>
           
           {/* Fixed Footer */}
           <div style={{
             padding: '20px 30px',
             borderTop: '1px solid rgba(100, 149, 237, 0.3)',
             display: 'flex',
             justifyContent: 'center',
             backgroundColor: 'rgba(0, 0, 0, 0.3)',
           }}>
             
             <button
                onClick={onConfirm}
                disabled={selectedCards.length === 0}
               style={{
                 padding: '12px 30px',
                 fontSize: '16px',
                 fontWeight: 'bold',
                 color: selectedCards.length === 0 ? '#666' : '#000',
                 backgroundColor: selectedCards.length === 0 ? 'rgba(100, 149, 237, 0.3)' : '#6495ed',
                 border: '2px solid transparent',
                 borderRadius: '30px',
                 cursor: selectedCards.length === 0 ? 'not-allowed' : 'pointer',
                 transition: 'all 0.3s ease',
                 textTransform: 'uppercase',
                 letterSpacing: '1px',
                 opacity: selectedCards.length === 0 ? 0.5 : 1,
               }}
               onMouseEnter={(e) => {
                 if (selectedCards.length > 0) {
                   e.currentTarget.style.transform = 'scale(1.05)';
                   e.currentTarget.style.boxShadow = '0 0 20px rgba(100, 149, 237, 0.5)';
                 }
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.transform = 'scale(1)';
                 e.currentTarget.style.boxShadow = 'none';
               }}
              >
                Add to Hand ({selectedCards.length}/{numSelectionsAllowed})
             </button>
            </div>
         </div>
       </div>
      );
    };

export default Game;